import React, { useCallback, useEffect, useRef, useState } from 'react';
import ScriptDialog from 'app/fuse-layouts/shared-components/ScriptDialog';
import OwnerSelectionCard from 'app/shared-components/OwnerSelectionCard';
import GlobalProvisionScriptsPreview from '../components/GlobalProvisionScriptsPreview';
import ClusterApplicationsSection from '../components/ClusterApplicationsSection';
import axios from 'axios';
import clsx from 'clsx';
import _ from '@lodash';
import Formsy from 'formsy-react';

// Redux imports
import { useDispatch, useSelector } from 'react-redux';
import * as StackActions from 'app/main/stacks/store/actions/stacks.actions.js';
import * as MarketActions from 'app/main/marketplace/store/actions';
import * as ActionsDialog from 'app/store/actions';
import * as Actions from '../store/actions';
import { CLUSTER_STATES, DisabledFormWrapper } from 'app/main/util/clusterUtils';

// Material-UI core components
import {
    Button, Dialog, DialogContent, DialogTitle, FormControl,
    FormControlLabel, Icon, IconButton, InputAdornment, Menu, MenuItem,
    Paper, Radio, RadioGroup,TextField, Typography, Checkbox, Switch,
    // new imports for card layout
    Card, CardHeader, CardContent, Box, Chip
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

// Fuse components and hooks
import { FuseAnimate, SelectFormsy, TextFieldFormsy } from '@fuse';
import { useForm } from '@fuse/hooks';
import { getClusterSettings } from 'app/main/settings/store/actions';
import { API_BASE_URL } from '@config';
import { RESOURCE_MANAGER, IMAGE_TYPE, ACCELERATOR_TYPES, ACCELERATOR_LABELS, IMAGE_MODE } from "../../util/enums";

// Custom styles and utility functions
import useStyles from 'app/cluster-styles/useStyles';
import { ipValid, setinstancesVol, validationInputUlt, validationMessUlt } from '../../util/InputValidate';
import { getZoneLabel } from '../util';
import { useInstanceAvailability } from '../hooks/useInstanceAvailability';
import { ZoneAvailabilityIndicator } from '../components/ZoneAvailabilityComponents';

// Card layout styles - matching Azure form styling
// Add inputGroupStyle to match AWS
const inputGroupStyle = {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
    marginBottom: '16px',
    alignItems: 'flex-start'
};
const tooltipIconStyle = {
    marginLeft: '8px',
    color: '#757575',
    fontSize: '18px'
};
const cardHeaderStyle = {
  backgroundColor: '#f7f9fc',
  paddingBottom: '8px',
  minHeight: '20px'
};
const sectionTitleStyle = {
  fontSize: '16px',
  fontWeight: 600,
  color: '#0f2147'
};
const cardStyle = {
    marginBottom: '24px',
    boxShadow: '0 2px 10px 0 rgba(0,0,0,0.1)',
    borderRadius: '8px',
    overflow: 'visible'
};
// Add these new styles for column layout
const columnContainerStyle = {
    display: 'flex',
    flexDirection: 'row',
    gap: '24px',
    width: '100%'
};
const columnStyle = {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0, // This prevents flex items from overflowing
};
// Custom help dialog component that shows detailed information in a dialog
const HelpDialog = ({ title, content, children }) => {
    const [open, setOpen] = React.useState(false);
    
    const handleOpen = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(true);
    };
    
    const handleClose = () => {
        setOpen(false);
    };
    
    return (
        <>
            <IconButton 
                size="small" 
                aria-label="help"
                style={{ padding: '4px' }} 
                disableRipple
                onClick={handleOpen}
            >
                {children}
            </IconButton>
            
            <Dialog 
                open={open} 
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">{title}</Typography>
                        <IconButton size="small" onClick={handleClose}>
                            <Icon>close</Icon>
                        </IconButton>
                    </div>
                </DialogTitle>
                <DialogContent style={{ paddingBottom: '24px' }}>
                    <Typography variant="body1" style={{ whiteSpace: 'pre-line', marginBottom: '16px' }}>
                        {content}
                    </Typography>
                </DialogContent>
            </Dialog>
        </>
    );
};

// Remove the tooltipProps as they're now included directly in the ClickableTooltip component
// and we have disabled all automatic triggers to handle clicks explicitly

const GcpCluster = props => {
    // Redux Hooks for State Management
    const dispatch = useDispatch();
    // Select various states from the Redux store
    const clusterSettings = useSelector(({ settings }) => settings.settings.clusterSettings);
	const cluster = useSelector(({ clusters }) => clusters.cluster.cluster);
    const clustersArr = useSelector(({ clusters }) => clusters.cluster.list);
    const error = useSelector(({ clusters }) => clusters.cluster.error);
    const providers = useSelector(({ providers }) => providers.providers.list);
    const loading = useSelector(({ fuse }) => fuse.settings.loading);
    const credentials = useSelector(({ credentials }) => credentials.credential.list);
    const supportedOSs = useSelector(({ clusters }) => clusters.cluster.os_types);
    const selectedMarketStack = useSelector(({ marketStack }) => marketStack.marketStack.stack);
    const marketplaceData = useSelector(({ marketStack }) => marketStack.marketStack.list);
    const globalProvisionPreview = useSelector(({ globalProvision }) => globalProvision?.preview);
    const stacks = useSelector(({ stacks }) => stacks.stack.list);
    const currentUser = useSelector(({ auth }) => auth.user);
    const users = useSelector(({ auth }) => auth.user.users) || [];

    // Refs and Custom Styles
    const classes = useStyles(props);
    const formRef = useRef(null);
    const pkgRef = useRef(null);
    const groupNodePrefixRefs = useRef([]);
    const textFieldRef = useRef(null);
    const isMountedRef = useRef(true);

    // Form Validation State
    const [isFormValid, setIsFormValid] = useState(true);
    const [selectedOwner, setSelectedOwner] = useState('');

    // Cluster and Provider States
    const [zones, setZones] = useState([]);
    const [regions, setRegions] = useState([]);
    const [nodeInstanceSizes, setNodeInstanceSizes] = useState(null);
    const [serverInstanceSizes, setServerInstanceSizes] = useState([]);

    // UI States
    const [prefixError, setPrefixError] = useState('');
    const [nodePrefixErrors, setNodePrefixErrors] = useState([]);
    const [filteredCredentials, setFilteredCredentials] = useState([]);
    const [deletedPkgs, setDeletedPkgs] = useState([]);
    const [cost, setCost] = useState(0);
    const [advanced, setAdvanced] = useState(false);

    // Popover and Menu States
    const [popovertarget, setPopovertarget] = useState(null);
    const [timeoutid, setTimeoutid] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);

    // Prefix Form States
    const [activeMenuIndex, setActiveMenuIndex] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [groupNodeInputValues, setGroupNodeInputValues] = useState({});

    // Quota Management States
    const [usedIntances, setUsedIntances] = useState(0);
    const [usedNetwork, setUsedNetwork] = useState(0);
    const [usedCpus, setUsedCpus] = useState(0);
    const [limitIntances, setLimitIntances] = useState(9999999);
    const [limitNetwork, setLimitNetwork] = useState(9999999);
    const [limitCpus, setLimitCpus] = useState(9999999);
    const [providerVolsize, setProviderVolsize] = useState({ min: 10, max: 1000 });
    const [clusterNames, setClusterNames] = useState(null);

    // Spot VM state
    const [spotVmEnabled, setSpotVmEnabled] = useState(false);

    // Capacity Reservation state
    const [enableCapacityReservation, setEnableCapacityReservation] = useState(false);

    // Custom Hook for Form Management
    const { form, handleChange, setForm } = useForm(props.oldForm || null);
    const [selectedShapes, setSelectedShapes] = useState([]);

    // Compute node instance types for availability checking
    const computeNodeInstanceTypes = React.useMemo(() => {
        if (!form || !form.data || !form.data.node || !form.data.node.group) {
            return [];
        }
        return form.data.node.group
            .map(function(node) { return node.instance_id; })
            .filter(Boolean);
    }, [form]);

    const {
        zoneAvailability,
        isZoneAvailable,
        getMissingInstances,
        isLoading: isAvailabilityLoading,
        error: availabilityError,
        unableToVerify
    } = useInstanceAvailability({
        provider: 'gcp',
        credentialId: form && form.credential_id,
        region: form && form.data && form.data.network && form.data.network.region_id,
        headNodeInstance: form && form.data && form.data.server && form.data.server.instance_id,
        computeNodeInstances: computeNodeInstanceTypes,
        enabled: Boolean(form && form.credential_id && form.data && form.data.network && form.data.network.region_id)
    });

    // Cleanup effect to handle component unmounting
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            // Clear any remaining timeouts
            if (timeoutid) {
                clearTimeout(timeoutid);
            }
        };
    }, [timeoutid]);

    //General Marketplace information:
    const market_id_value = selectedMarketStack?.id
    const marketId = form?.info?.market_id ?? market_id_value
    const isMarketStack = Boolean(selectedMarketStack)
    const currentMarketStack = marketplaceData?.find(item => item.id === marketId) || selectedMarketStack

    //Marketplace header information
    const marketName = currentMarketStack?.name
    const marketVersion = currentMarketStack?.version
    const title = currentMarketStack ? (marketName + ' ' + marketVersion) : form?.name || 'New Cluster'
    const imageSrc = currentMarketStack?.logo
        ? `assets/images/logos/${currentMarketStack?.logo}`
        : `assets/images/providers/gcp.png`;

    //Marketplace form information
    const hasMarketplaceConfig = Boolean(currentMarketStack?.config)
    const marketplaceOSTypes = currentMarketStack?.config?.os_types;
    const marketplaceInstanceIds = currentMarketStack?.config?.instance_type;
    const marketplaceComputeIds = currentMarketStack?.config?.computeNodeSize;
    const marketplaceManager = currentMarketStack?.config?.resource_manager;

    // Marketplace app profile flags (data-driven behavior)
    const marketplaceSkipSubscription = hasMarketplaceConfig && currentMarketStack.config.skip_subscription === true;
    const marketplaceHideProvisionScript = hasMarketplaceConfig && currentMarketStack.config.hide_provision_script === true;
    const marketplaceAllowImageSelection = hasMarketplaceConfig && currentMarketStack.config.allow_image_selection === true;
    const marketplaceDisableGlobalScripts = hasMarketplaceConfig && currentMarketStack.config.disable_global_scripts === true;
    const [isVncTokenAuthentication, setIsVncTokenAuthentication] = useState(false);
    const [isVncRemoteClient, setIsVncRemoteClient] = useState(false);
	const [disableInputs, setDisableInputs] = useState(false);
    const [isScriptOpen, setScriptOpen] = useState(false);

    // Global provision scripts state (Feature 002 enhancement)
    const [excludedGlobalScripts, setExcludedGlobalScripts] = useState([]);

    // Cluster applications state (Feature: cluster-apps)
    const [clusterApplications, setClusterApplications] = useState([]);

    // Image type state variables
    const [serverImageType, setServerImageType] = useState(IMAGE_TYPE.HEIDI_STACK);
    const [nodeImageTypes, setNodeImageTypes] = useState({});
    const [imageMode, setImageMode] = useState(IMAGE_MODE.MULTI_IMAGE);

    useEffect(() => {
        if (
            isMarketStack &&
            marketplaceManager &&
            form?.data &&
            form.data.manager !== marketplaceManager
        ) {
            setForm(prevForm => {
                if (!prevForm || !prevForm.data) return prevForm;
                return {
                    ...prevForm,
                    data: {
                        ...prevForm.data,
                        manager: marketplaceManager
                    }
                };
            });
        }
    }, [isMarketStack, marketplaceManager, form?.data?.manager, setForm]);

	useEffect(() => {
		if(props.type === 'edit' && form && form.info && form.info.state !== CLUSTER_STATES.DESTROYED) {
			setDisableInputs(true);
		}
	}, [props, form])

    // Separate state for script management - only disable upload/delete, not view
    const [disableScriptModification, setDisableScriptModification] = useState(false);
    
    useEffect(() => {
        if(props.type === 'edit' && form && form.info && form.info.state !== CLUSTER_STATES.DESTROYED) {
            setDisableScriptModification(true);
        }
    }, [props, form])

    // Load excluded global scripts when editing
    useEffect(() => {
        if (props.type === 'edit' && form) {
            // Check both locations - data.excludedGlobalScripts and root level excludedGlobalScripts
            const excluded = form.data?.excludedGlobalScripts || form.excludedGlobalScripts;
            if (excluded && excluded.length > 0) {
                setExcludedGlobalScripts(excluded);
            }
        }
    }, [props.type, form?.data?.excludedGlobalScripts, form?.excludedGlobalScripts]);

    // Auto-exclude ALL global provision scripts when marketplace config has disable_global_scripts
    useEffect(() => {
        if (marketplaceDisableGlobalScripts && globalProvisionPreview?.configs?.length > 0) {
            const allScriptIds = [];
            globalProvisionPreview.configs.forEach(function(config) {
                if (config.scripts) {
                    config.scripts.forEach(function(script) {
                        allScriptIds.push(config._id + '-' + script._id);
                    });
                }
            });
            if (allScriptIds.length > 0) {
                setExcludedGlobalScripts(allScriptIds);
            }
        }
    }, [marketplaceDisableGlobalScripts, globalProvisionPreview?.configs]);

    // Load cluster applications when editing or from marketplace pre-populated data
    useEffect(() => {
        if (form) {
            const apps = form.applications;
            if (apps && apps.length > 0) {
                setClusterApplications(apps);
            }
        }
    }, [props.type, form?.applications]);

    useEffect(() => {
        const names = clustersArr.map(cluster => cluster.name);
        setClusterNames(names);
    }, [clustersArr, setClusterNames]);
    useEffect(() => {
        dispatch(StackActions.getStacks());
		dispatch(getClusterSettings());
    }, [dispatch]);

    // Set VNC values only once when form loads or clusterSettings changes
    const vncInitialized = useRef(false);
    useEffect(() => {
        if (vncInitialized.current) return;
        
        const setVncValues = () => {
            if (form && form.info) {
                setIsVncTokenAuthentication(form.info.vncTokenAuthentication !== undefined ? form.info.vncTokenAuthentication : clusterSettings?.vncTokenAuthentication);
                setIsVncRemoteClient(form.info.vncRemoteClient !== undefined ? form.info.vncRemoteClient : clusterSettings?.vncRemoteClient);
                vncInitialized.current = true;
            } else if (clusterSettings) {
                setIsVncTokenAuthentication(clusterSettings.vncTokenAuthentication);
                setIsVncRemoteClient(clusterSettings.vncRemoteClient);
                vncInitialized.current = true;
            }
        };

        setVncValues();
    }, [form?.info?.vncTokenAuthentication, form?.info?.vncRemoteClient, clusterSettings]);

    // Set fast capacity validation default for new clusters
    useEffect(() => {
        if (props.type === 'new' && clusterSettings?.fastCapacityValidationDefault !== undefined) {
            setEnableCapacityReservation(clusterSettings.fastCapacityValidationDefault);
        }
    }, [props.type, clusterSettings]);

    // Initialize selectedOwner when editing existing clusters
    useEffect(() => {
        if (props.type === 'edit' && form && form.user && form.user.length > 0) {
            setSelectedOwner(form.user[0]);
        }
    }, [props.type, form]);

    useEffect(() => {
        dispatch(MarketActions.getMarketplaceStacks(marketplaceData))
    }, [dispatch, marketplaceData])

    // Initialize image types from form data
    useEffect(() => {
        if (form?.data?.node?.group) {
            const initialTypes = {};
            form.data.node.group.forEach((node, index) => {
                initialTypes[index] = node.image_type || IMAGE_TYPE.HEIDI_STACK;
            });
            setNodeImageTypes(initialTypes);
        }
    }, [form?.data?.node?.group]);
    
    useEffect(() => {
        if (form?.data?.server?.image_type) {
            setServerImageType(form.data.server.image_type);
        }
    }, [form?.data?.server?.image_type]);

    useEffect(() => {
        // Initialize imageMode from form data when editing
        if (form?.info?.imageMode) {
            setImageMode(form.info.imageMode);
        }
    }, [form?.info?.imageMode]);

    // Sync node groups with head node settings when in UNIFIED mode
    useEffect(() => {
        if (imageMode === IMAGE_MODE.UNIFIED && form?.data?.node?.group && form?.data?.server?.image_name) {
            // Update all node groups to use server's image_name as their prefix and image_type
            const updatedGroups = form.data.node.group.map(node => ({
                ...node,
                group_node_prefix: form.data.server.image_name,
                image_type: serverImageType
            }));
            
            // Only update if there are actual changes to avoid infinite loops
            const needsUpdate = form.data.node.group.some((node, index) => 
                node.group_node_prefix !== updatedGroups[index].group_node_prefix ||
                node.image_type !== updatedGroups[index].image_type
            );
            
            if (needsUpdate) {
                setForm(prevForm => ({
                    ...prevForm,
                    data: {
                        ...prevForm.data,
                        node: {
                            ...prevForm.data.node,
                            group: updatedGroups
                        }
                    }
                }));
                
                // Also update nodeImageTypes state to match
                const updatedNodeImageTypes = {};
                form.data.node.group.forEach((_, index) => {
                    updatedNodeImageTypes[index] = serverImageType;
                });
                setNodeImageTypes(updatedNodeImageTypes);
            }
        }
    }, [imageMode, form?.data?.server?.image_name, serverImageType]);

    // Initialize spot VM from form data
    useEffect(() => {
        if (form?.data?.spot_vm !== undefined) {
            setSpotVmEnabled(form.data.spot_vm);
        }
    }, [form?.data?.spot_vm]);

    // Initialize capacity reservation from form data (when editing existing clusters)
    useEffect(() => {
        if (props.type === 'edit' && form?.data?.network) {
            if (form.data.network.enable_capacity_reservation !== undefined) {
                setEnableCapacityReservation(form.data.network.enable_capacity_reservation);
            }
        }
    }, [props.type, form?.data?.network]);

    // Ensure accelerator fields in form state are properly initialized from cluster data
    const acceleratorInitialized = useRef(false);
    useEffect(() => {
        if (acceleratorInitialized.current) return;
        if (!form?.data) return;
        
        let needsServerUpdate = false;
        let needsNodeUpdate = false;
        
        if (form?.data?.server) {
            // Check if server accelerator fields need initialization
            if (form.data.server.accelerator_type === undefined) {
                needsServerUpdate = true;
            }
        }
        
        // Check if any node needs accelerator initialization
        if (form?.data?.node?.group && form.data.node.group.length > 0) {
            needsNodeUpdate = form.data.node.group.some(node => 
                node.accelerator_type === undefined || node.accelerator_count === undefined
            );
        }
        
        if (needsServerUpdate || needsNodeUpdate) {
            acceleratorInitialized.current = true;
            setForm(prevForm => {
                if (!prevForm?.data) return prevForm;
                
                let newData = { ...prevForm.data };
                
                if (needsServerUpdate && prevForm.data.server) {
                    newData.server = {
                        ...prevForm.data.server,
                        accelerator_type: prevForm.data.server.accelerator_type !== undefined ? prevForm.data.server.accelerator_type : null,
                        accelerator_count: prevForm.data.server.accelerator_count !== undefined ? prevForm.data.server.accelerator_count : 0
                    };
                }
                
                if (needsNodeUpdate && prevForm.data.node?.group) {
                    newData.node = {
                        ...prevForm.data.node,
                        group: prevForm.data.node.group.map(node => ({
                            ...node,
                            accelerator_type: node.accelerator_type !== undefined ? node.accelerator_type : null,
                            accelerator_count: node.accelerator_count !== undefined ? node.accelerator_count : 0
                        }))
                    };
                }
                
                return { ...prevForm, data: newData };
            });
        } else {
            acceleratorInitialized.current = true;
        }
    }, [form?.data, setForm]);

    const setClusterInfo = (e) => {
        setForm(_.set({ ...form }, 'data', { ...form.data, moab: { ...form.data.moab, [e.target.name]: e.target.value } }));
    }

    useEffect(() => {
        if (props.type === 'new') {
            selectedMarketStack ?
                dispatch(Actions.newMarketCluster(selectedMarketStack)) :
                dispatch(Actions.newCluster());
        }
    }, [props.type, selectedMarketStack, dispatch]);
    
    // Ensure form validation is properly initialized
    useEffect(() => {
        // This helps ensure the form validation state is set correctly on initial load
        if (formRef.current && form) {
            const timeoutId = setTimeout(() => {
                if (isMountedRef.current && formRef.current && formRef.current.validateForm()) {
                    setIsFormValid(true);
                }
            }, 100);
            
            return () => clearTimeout(timeoutId);
        }
    }, [form]);

    useEffect(() => {
        if (form?.data.node.group) {
            form.data.node.group.forEach(node => setSelectedShapes(prevSelectedShapes => [...prevSelectedShapes, node.instance_id]));
        }
    }, [form]);

    useEffect(() => {
        if (form?.data.network.region_id) {
            let costNodesTotal = 0;
            let costNode = 0;
            const sps = providers.filter(p => p.id === "gcp");
            
            // Safety check: ensure GCP provider and instanceSizes exist
            if (!sps[0] || !sps[0].instanceSizes) {
                return;
            }
            
            const headNode = sps[0].instanceSizes.filter(r => r.instance_id === form.data.server.instance_id);
            const costHeadNode = headNode[0]?.cost?.cost_region[form.data.network.region_id];

            form.data.node.group.map(val => {
                const node = sps[0].instanceSizes.filter(r => r.instance_id === val.instance_id);
                const costNodes = node[0]?.cost?.cost_region[form.data.network.region_id];
                if (costNodes) {
                    costNode = costNodes * val.count;
                    costNodesTotal = costNodesTotal + costNode;
                }
                return 0;
            });
            setCost(costNodesTotal + costHeadNode);
        }
    }, [form, providers]);

    useEffect(() => {
        const err = error || props.error;
        if (err && err.type === 'form') {
            const name = { name: err.message };
            formRef.current &&
                formRef.current.updateInputsWithError({
                    ...name
                });
            disableButton();
        }
    }, [error, props.error]);
    //prefix:
    useEffect(() => {
        setInputValue(form?.info?.prefix);
    }, [form]);

    useEffect(() => {
        if ((cluster && !form) || (cluster && form && cluster.id !== form.id)) {
            // Ensure the provision object with script field is initialized
            const clusterWithProvision = {
                ...cluster,
                data: {
                    ...cluster.data,
                    provision: {
                        ...cluster.data.provision || {},
                        script: cluster.data?.provision?.script || ''
                    }
                }
            };
            setForm(clusterWithProvision);
        }
    }, [form, cluster, setForm]);

    useEffect(() => {
        if (form && form.data.network.region_id) {
            providers.map(
                p =>
                    p.id === "gcp" &&
                    p.regions.map(r => r.zones && r.region_id === form.data.network.region_id && setZones(r.zones))
            );
        }
    }, [providers, form]);
    //Set instance sizes and node sizes here
    useEffect(() => {
        if (
            providers &&
            form &&
            (!nodeInstanceSizes || !serverInstanceSizes || !filteredCredentials || !regions || !zones)
        ) {
            const P = providers.find(provider => provider.id === "gcp");
            const InstSizes = P.instanceSizes;
            const Regions = P.regions;
            if (props.type !== 'new') {
                if (InstSizes.filter(s => s.instance_id === form.data.server.instance_id).length === 0) {
                    InstSizes.push({ instance_id: form.data.server.instance_id, name: form.data.server.instance_id, cores: 0 });
                }
                if (Regions.filter(r => r.region_id === form.data.network.region_id).length === 0) {
                    Regions.push({
                        region_id: form.data.network.region_id,
                        name: form.data.network.region_id,
                        zones: [form.data.network.zone_id]
                    });
                }

                for (const gs of form.data.node.group) {
                    let contains = false;
                    for (const s of InstSizes) {
                        if (s.instance_id === gs.instance_id) {
                            contains = true;
                        }
                    }

                    if (!contains) {
                        InstSizes.push({ instance_id: gs.instance_id, name: gs.instance_id, cores: 0 });
                    }
                }
            }
            const C = credentials.filter(cred => cred.provider === "gcp");
            setNodeInstanceSizes(InstSizes);
            setServerInstanceSizes(InstSizes);
            setFilteredCredentials(C);
            setRegions(Regions);
            if (form.data.network.region_id) {
                Regions.map(r => r.zones && r.region_id === form.data.network.region_id && setZones(r.zones));
            }
        }
    }, [
        providers,
        props,
        form,
        credentials,
        nodeInstanceSizes,
        serverInstanceSizes,
        filteredCredentials,
        regions,
        zones
    ]);

    useEffect(() => {
        // Function to set the initial values for the node group and server instance
        const setInitialValues = () => {
            let initialNodeInstanceId = nodeInstanceSizes[0].instance_id;
            let initialServerInstanceId = serverInstanceSizes[0].instance_id;

            if (currentMarketStack) {
                initialNodeInstanceId = currentMarketStack.config?.computeNodeSize?.[0] ?? initialNodeInstanceId;
                initialServerInstanceId = currentMarketStack.config?.instance_type?.[0] ?? initialServerInstanceId;
            }
            form.data.node.group.forEach(groupItem => {
                if (!groupItem.instance_id) {
                    groupItem.instance_id = initialNodeInstanceId
                }
                // Ensure accelerator fields exist
                if (groupItem.accelerator_type === undefined) {
                    groupItem.accelerator_type = null;
                }
                if (groupItem.accelerator_count === undefined) {
                    groupItem.accelerator_count = 0;
                }
            })
            if (!form.data.server.instance_id) form.data.server.instance_id = initialServerInstanceId
            return { ...form }
        };
        if (form && form.data.node.group.length === 1 && !form.data.node.group[0].instance_id && nodeInstanceSizes) {
            setForm(setInitialValues())
        }
    }, [form, currentMarketStack, nodeInstanceSizes, serverInstanceSizes, setForm]);


    useEffect(() => {
        if (form && form.data.node.group.length === 1 && !form.data.node.group[0].instance_id && nodeInstanceSizes) {
            const group = form.data.node.group;
            group[0].instance_id = nodeInstanceSizes[0].instance_id;
            group[0].count = props.job ? props.job.data.nodes : '0';
            // Ensure accelerator fields exist
            if (group[0].accelerator_type === undefined) {
                group[0].accelerator_type = null;
            }
            if (group[0].accelerator_count === undefined) {
                group[0].accelerator_count = 0;
            }
            setForm(_.set({ ...form }, 'data', { ...form.data, node: { ...form.data.node, group: [...group] } }));
        }
        if (form && !form.data.server.instance_id && serverInstanceSizes.length > 0) {
            setForm(
                _.set({ ...form }, 'data', {
                    ...form.data,
                    server: { ...form.data.server, instance_id: serverInstanceSizes[0].instance_id }
                })
            );
        }
    }, [form, serverInstanceSizes, props, nodeInstanceSizes, setForm]);

    useEffect(() => {
        if (form && form.data.server.disk_type !== form.data.node.disk_type) {
            setNodeObj({ name: 'disk_type', value: form.data.server.disk_type });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form]);
    // These are for the PopOver, the validation has been moved to ./InputValidate.js
    //node volumen prop
    useEffect(() => {
        setProviderVolsize(setinstancesVol("gcp", isMarketStack)); //set Volume limit depending on provider
        dispatch(Actions.provider_os_types("gcp", isMarketStack))
    }, [dispatch, isMarketStack]);

    // Auto-select OS type when marketplace app restricts supported OS types
    useEffect(() => {
        if (marketplaceOSTypes && supportedOSs && supportedOSs.length > 0 && form) {
            const allowedOSs = supportedOSs.filter(os => marketplaceOSTypes.includes(os));
            if (allowedOSs.length > 0 && !allowedOSs.includes(form.info['os-type'])) {
                setInfoObj({ name: 'os-type', value: allowedOSs[0] });
            }
        }
    }, [marketplaceOSTypes, supportedOSs]);

    //node volumen prop
    useEffect(() => {
        if (form?.data.network.region_id) {
            const sps = providers.filter(p => p.id === "gcp");
            const region = sps[0].regions.filter(r => r.region_id === form.data.network.region_id);
            const zone = region[0]?.zones?.filter(
                r => r.zone_id === form.data.network.availability_domain || r.zone_id === form.data.network.zone_id
            );

            const clustersInUse = clustersArr.filter(c => c.info.state !== 'down' && c.provider === "gcp");
            let intances = clustersInUse.length;
            let network = clustersInUse.length;
            let cpus = 0;

            clustersInUse.forEach(c => {
                const fp = providers.filter(p => p.id === c.provider);
                //nodes used
                c.data.node.group.forEach(i => {
                    intances = parseInt(intances) + parseInt(i.count);
                    const ccores = fp[0].instanceSizes.filter(co => co.instance_id === i.instance_id);
                    cpus = cpus + parseInt(ccores[0]?.cores) * i.count;
                });
                //server used
                const scores = fp[0].instanceSizes.filter(co => co.instance_id === c.data.server.instance_id);
                cpus = cpus + parseInt(scores[0].cores);
            });
            //server edict
            const ecores = sps[0].instanceSizes.filter(co => co.instance_id === form.data.server.instance_id);
            network++;
            intances++;
            cpus = cpus + parseInt(ecores[0].cores);
            form.data.node.group.forEach(i => {
                intances = parseInt(intances) + parseInt(i.count);
                const eccores = sps[0].instanceSizes.filter(co => co.instance_id === i.instance_id);
                if (eccores[0]?.cores) cpus = cpus + parseInt(eccores[0].cores) * i.count;
            });
            setUsedCpus(cpus);
            setUsedIntances(intances);
            setUsedNetwork(network);

            zone?.[0]?.quotas?.cpus && setLimitIntances(zone[0]?.quotas?.cpus);
            zone?.[0]?.quotas?.intance && setLimitCpus(zone[0]?.quotas?.intance);
            zone?.[0]?.quotas?.network && setLimitNetwork(zone[0]?.quotas?.network);
        }
    }, [form, providers, clustersArr]);

    // helper functions
    const getDefaultOsType = () => {
        // If marketplace restricts OS types, prioritize the marketplace OS
        if (marketplaceOSTypes && supportedOSs) {
            const allowedOSs = supportedOSs.filter(os => marketplaceOSTypes.includes(os));
            if (allowedOSs.length > 0) {
                if (form.info['os-type'] && allowedOSs.includes(form.info['os-type'])) {
                    return form.info['os-type'];
                }
                return allowedOSs[0];
            }
        }
        if (supportedOSs.includes(form.info['os-type'])) {
            return form.info['os-type'];
        }
        return supportedOSs[0] || '';
    };

    const handleClose = useCallback(() => {
        dispatch(ActionsDialog.closeDialog());
        dispatch(MarketActions.clearStack());
    }, [dispatch]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                handleClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleClose]);

    const disableButton = () => setIsFormValid(false);
    const enableButton = () => setIsFormValid(true);

    const handleMenuOpen = (event) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleGroupNodeMenuItemClick = (index, selectedPrefix) => {
        setGroupNodeInputValues(prevValues => ({
            ...prevValues,
            [index]: selectedPrefix
        }));
        setNodePrefix(index, selectedPrefix);
        setActiveMenuIndex(null);
    };
    //group prefix:
    const handleGroupNodeMenuOpen = (index) => {
        setActiveMenuIndex(index);
    };

    const handleDeletePackages = pkg => {
        const filteredPkgs = form.data.packages.filter(p => p !== pkg);
        setDataObj({ name: 'packages', value: filteredPkgs });
        const isInPkgs = form.data.packages.some(p => p === pkg);
        const isInDeletedPkgs = deletedPkgs.some(dp => dp === pkg);
        if (isInPkgs && !isInDeletedPkgs) {
            const delArr = form.data.packages.filter(p => p === pkg);
            setDeletedPkgs([...deletedPkgs, ...delArr]);
        }
    };

    const setDataObj = ({ name, value }) => {
        setForm(_.set({ ...form }, 'data', { ...form.data, [name]: value }));
    };

    const setNetworkObj = ({ name, value }) => {
        setForm(_.set({ ...form }, 'data', { ...form.data, network: { ...form.data.network, [name]: value } }));
    };
    const setNodeObj = ({ name, value }) => {
        setForm(_.set({ ...form }, 'data', { ...form.data, node: { ...form.data.node, [name]: value } }));
    };
    const combineSameConfigurationNodes = groups => {
        const seenGroups = {};
        groups.forEach(group => {
            const compositeId = `${group.group_node_prefix_label}-${group.group_node_prefix}-${group.instance_id}-${group.ocpu}-${group.mem}-${group.accelerator_type}-${group.accelerator_count}`;
            if (compositeId in seenGroups)
                seenGroups[compositeId].count = parseInt(seenGroups[compositeId].count) + parseInt(group.count);
            else seenGroups[compositeId] = group;
        });
        const filteredGroups = Object.keys(seenGroups).map(key => seenGroups[key]);
        return filteredGroups;
    };
    const setInstanceId = (i = 0, value) => {
        //Fix the issue of entering wrong CPUS and MEM for Flex instance will not allow you to Save the Cluster info if the user
        //decided to choose a normal / NON Flex instance
        const groups = form.data.node.group;
        groups[i]['instance_id'] = value;
        if (!value.includes('Flex')) {
            setNodeOCPUS(i, 1);
            setNodeMEM(i, 1);
        }
        // Clear accelerator settings when switching to a non-N1 instance (e.g., G2, A2, A3 have built-in GPUs)
        if (!value.startsWith('n1-')) {
            groups[i]['accelerator_type'] = null;
            groups[i]['accelerator_count'] = 0;
        }
        const groupFiltered = combineSameConfigurationNodes(groups);
        setForm(_.set({ ...form }, 'data', { ...form.data, node: { ...form.data.node, group: [...groupFiltered] } }));
    };
    const setNodePrefix = (i = 0, value) => {
        const groups = form.data.node.group;
        groups[i]['group_node_prefix'] = value;
        const groupFiltered = combineSameConfigurationNodes(groups);
        setForm(_.set({ ...form }, 'data', { ...form.data, node: { ...form.data.node, group: [...groupFiltered] } }));
    };
    const setNodePrefixLabel = (i = 0, value) => {
        const groups = form.data.node.group;
        groups[i]['group_node_prefix_label'] = value;
        const groupFiltered = combineSameConfigurationNodes(groups);
        setForm(_.set({ ...form }, 'data', { ...form.data, node: { ...form.data.node, group: [...groupFiltered] } }));
    }
    const setNodeMEM = (i, value) => {
        const groups = form.data.node.group;
        groups[i].mem = value;
        const groupFiltered = combineSameConfigurationNodes(groups);
        setForm(_.set({ ...form }, 'data', { ...form.data, node: { ...form.data.node, group: [...groupFiltered] } }));
    };
    const setInstanceCount = (i, { name, value }) => {
        const group = form.data.node.group;
        group[i][name] = value;

        const groupFiltered = combineSameConfigurationNodes(group);
        setForm(_.set({ ...form }, 'data', { ...form.data, node: { ...form.data.node, group: [...groupFiltered] } }));
    };
    const setServerObj = ({ name, value }) => {
        //Fix the issue of entering wrong CPUS and MEM for Flex instance will not allow you to Save the Cluster info if the user
        //decided to choose a normal / NON Flex instance
        if (name === 'instance_id' && !value.includes('Flex')) {
            form.data.server.ocpus = form.data.server.mem = 1;
            setForm(_.set({ ...form }, 'data', { ...form.data, server: { ...form.data.server, [name]: value } }));
        } else {
            setForm(_.set({ ...form }, 'data', { ...form.data, server: { ...form.data.server, [name]: value } }));
        }
    };
    const setInfoObj = ({ name, value }) => {
        setForm(_.set({ ...form }, 'info', { ...form.info, [name]: value }));
    };

    const setNodeOCPUS = (i, value) => {
        const groups = form.data.node.group;
        groups[i].ocpus = value;
    };
    const setPackagesArr = () => {
        const pkg = pkgRef.current.value;
        setForm(_.set({ ...form }, 'data', { ...form.data, packages: [...form.data.packages, pkg] }));
        pkgRef.current.value = null;
    };
    const handleUpload = e => {
        const file = e.target.files[0];
        if (!file) return;

        //reset value to '' so that the same file can be uploaded if removed
        e.target.value = '';

        const reader = new FileReader();
        reader.readAsBinaryString(file);
        reader.onload = () => {
            setForm(
                _.set({ ...form }, 'data', { ...form.data, provision: { ...form.data.provision, script: reader.result } })
            );
        };
    };

    //Validation functions
    const validationInput = (type, instanceid, currentvalue, ocpus, provider, currentMarketStack) => {
        return validationInputUlt(type, instanceid, currentvalue, ocpus, provider, currentMarketStack);
    };

    const validationMess = (type, instanceid, ocpus, provider, currentMarketStack) => {
        return validationMessUlt(type, instanceid, ocpus, provider, currentMarketStack);
    };

    const handleDeleteScript = () => {
        setForm(_.set({ ...form }, 'data', { ...form.data, provision: { ...form.data.provision, script: '' } }));
    };

    // Image type handler functions
    const handleServerImageTypeChange = (e) => {
        const newValue = e.target.value;
        setServerImageType(newValue);
        setForm(prevForm => ({
            ...prevForm,
            data: {
                ...prevForm.data,
                server: {
                    ...prevForm.data.server,
                    image_type: newValue
                }
            }
        }));
    };

    const setNodeImageType = (index, type) => {
        setNodeImageTypes(prev => ({
            ...prev,
            [index]: type
        }));
        
        setForm(prevForm => {
            const updatedGroups = [...prevForm.data.node.group];
            updatedGroups[index] = {
                ...updatedGroups[index],
                image_type: type
            };
            return {
                ...prevForm,
                data: {
                    ...prevForm.data,
                    node: {
                        ...prevForm.data.node,
                        group: updatedGroups
                    }
                }
            };
        });
    };

    const areNonFormsyRequiredFieldsValid = () => {
        // check that the server prefix and all of the prefixes of compute nodes are in a non-error state and have content
        const serverPrefixisValid = !prefixError && form.data.server.image_name
        // In unified mode, node prefixes are auto-synced from server, so skip validation
        const nodePrefixesAreValid = imageMode === IMAGE_MODE.UNIFIED 
            ? true 
            : (nodePrefixErrors.every(error => error === '') && form.data.node.group.every(node => node.group_node_prefix))
        return serverPrefixisValid && nodePrefixesAreValid
    }

    const addComputeNode = () => {
        if (form.data.node.group.length < 250) {
            // In unified mode, use server's image_name as the node prefix and inherit image_type
            const unifiedPrefix = imageMode === IMAGE_MODE.UNIFIED ? form.data.server.image_name : '';
            const unifiedImageType = imageMode === IMAGE_MODE.UNIFIED ? serverImageType : IMAGE_TYPE.HEIDI_STACK;
            
            const initialValues = { 
                ...form.data.node.group[0], 
                count: 1,
                accelerator_type: null,
                accelerator_count: 0,
                group_node_prefix: imageMode === IMAGE_MODE.UNIFIED ? unifiedPrefix : form.data.node.group[0].group_node_prefix,
                image_type: imageMode === IMAGE_MODE.UNIFIED ? unifiedImageType : (form.data.node.group[0].image_type || IMAGE_TYPE.HEIDI_STACK)
            };
            setForm((prevForm) => ({
                ...prevForm, data: { ...prevForm.data, node: { ...prevForm.data.node, group: [...prevForm.data.node.group, initialValues] } }
            }
            ));
        }
    }

    // Accelerator selection

    useEffect(() => {
        if (form?.data?.server?.instance_id && !form.data.server.instance_id.startsWith('n1-')) {
            setForm(prevForm => ({
                ...prevForm,
                data: {
                    ...prevForm.data,
                    server: {
                        ...prevForm.data.server,
                        accelerator_type: null,
                        accelerator_count: 0
                    }
                }
            }));
        }
    }, [form?.data?.server?.instance_id]);

    useEffect(() => {
        // Clear accelerator settings for any node group that has a non-N1 instance
        // (G2, A2, A3 have built-in GPUs and don't support guest accelerators)
        const hasNonN1Instance = form?.data?.node?.group?.some(node => node.instance_id && !node.instance_id.startsWith('n1-'));
        if (hasNonN1Instance) {
            setForm(prevForm => {
                const updatedGroups = prevForm.data.node.group.map(group => {
                    if (group.instance_id && !group.instance_id.startsWith('n1-')) {
                        return {
                            ...group,
                            accelerator_type: null,
                            accelerator_count: 0
                        };
                    }
                    return group;
                });
                return {
                    ...prevForm,
                    data: {
                        ...prevForm.data,
                        node: {
                            ...prevForm.data.node,
                            group: updatedGroups
                        }
                    }
                };
            });
        }
    }, [form?.data?.node?.group?.map(g => g.instance_id).join(',')]);
    
    
    const setServerAcceleratorType = (e) => {
        const { name, value } = e.target;
        setForm(prevForm => {
            const instanceId = prevForm?.data?.server?.instance_id || '';
            const isN1Instance = instanceId.startsWith('n1-');
    
            return {
                ...prevForm,
                data: {
                    ...prevForm.data,
                    server: {
                        ...prevForm.data.server,
                        [name]: isN1Instance ? value : null,
                        accelerator_count: isN1Instance && value ? (prevForm.data.server.accelerator_count || 0) : 0
                    }
                }
            };
        });
    };

    const setAcceleratorType = (e) => {
        const { name, value } = e.target;
        setForm(prevForm => {
            const hasNonN1Instance = prevForm.data.node.group.some(node => !node.instance_id.startsWith('n1-'));
    
            return {
                ...prevForm,
                data: {
                    ...prevForm.data,
                    node: {
                        ...prevForm.data.node,
                        [name]: hasNonN1Instance ? null : value,
                        accelerator_count: hasNonN1Instance ? 0 : (value ? 1 : 0)
                    }
                }
            };
        });
    };
    
    const removeComputeNode = (i) => {
        if (form.data.node.group.length > 1) {
            setForm((prevForm) => {
                const group = [...prevForm.data.node.group]
                group.splice(i, 1)
                return { ...prevForm, data: { ...prevForm.data, node: { ...prevForm.data.node, group } } }
            })
        }
    }

    const handleEdit = ({ confirmed }) => {
        if (confirmed) {
            dispatch(ActionsDialog.closeDialog());
        }
        if (error) {
            dispatch(Actions.clearError());
        }
        //reset lastDeployTime & lastDestroyTime
        if (form?.info?.lastDeployTime) form.info.lastDeployTime = 0;
        if (form?.info?.lastDestroyTime) form.info.lastDestroyTime = 0;

        // Create a copy of the form like AWS handleEdit does
        const updatedForm = {
            ...form,
            data: {
                ...form.data,
            }
        };

        // Apply image types to form data
        updatedForm.data.server.image_type = serverImageType;
        updatedForm.data.spot_vm = spotVmEnabled;
        
        // Apply capacity reservation settings
        updatedForm.data.network = {
            ...updatedForm.data.network,
            enable_capacity_reservation: enableCapacityReservation
        };
        
        // In unified mode, all nodes use the server's image settings
        updatedForm.data.node.group.forEach((node, index) => {
            node.image_type = imageMode === IMAGE_MODE.UNIFIED 
                ? serverImageType 
                : (nodeImageTypes[index] || IMAGE_TYPE.HEIDI_STACK);
            node.group_node_prefix = imageMode === IMAGE_MODE.UNIFIED 
                ? form.data.server.image_name 
                : node.group_node_prefix;
        });

        updatedForm.data.node.group.forEach(node => {
            if (node.group_node_prefix === '') node.group_node_prefix = updatedForm.info.prefix
        });
        
        // Ensure accelerator fields are included for server and nodes
        if (!updatedForm.data.server.accelerator_type) {
            updatedForm.data.server.accelerator_type = null;
        }
        if (!updatedForm.data.server.accelerator_count && updatedForm.data.server.accelerator_count !== 0) {
            updatedForm.data.server.accelerator_count = 0;
        }
        updatedForm.data.node.group.forEach(node => {
            if (!node.accelerator_type) {
                node.accelerator_type = null;
            }
            if (!node.accelerator_count && node.accelerator_count !== 0) {
                node.accelerator_count = 0;
            }
        });
        

        // Set imageMode in info object (matches schema)
        updatedForm.info.imageMode = imageMode;

        // Add excluded global provision scripts (Feature 002 enhancement)
        if (excludedGlobalScripts && excludedGlobalScripts.length > 0) {
            updatedForm.excludedGlobalScripts = excludedGlobalScripts;
        } else {
            updatedForm.excludedGlobalScripts = [];
        }

        // Add cluster applications (Feature: cluster-apps)
        updatedForm.applications = clusterApplications || [];

        // Add owner selection if super-admin selected a different owner
        let ownerUsername;
        if (currentUser.role && currentUser.role.includes('super-admin') && selectedOwner) {
            ownerUsername = selectedOwner;
            updatedForm.user = [ownerUsername];
            const selectedUser = users.find(user => {
                return user.name === selectedOwner;
            });
            if (selectedUser) {
                updatedForm.owner = selectedUser.id; // Use 'id' instead of '_id'
            }
        }

        dispatch(Actions.editCluster(updatedForm, deletedPkgs, ownerUsername));
    };

    const handleSave = ({ confirmed }) => {

        if (props.setOldForm) {
            props.setOldForm(form);
        }
        
        if (confirmed) {
            dispatch(ActionsDialog.closeDialog());
        }
        const updatedForm = {
            ...form,
            data: {
                ...form.data,
                spot_vm: spotVmEnabled,
                server: {
                    ...form.data.server,
                    image_type: serverImageType
                },
                network: {
                    ...form.data.network,
                    enable_capacity_reservation: enableCapacityReservation
                }
            },
            info: {
                ...form.info,
                isMarketplace: isMarketStack,
                market_id: market_id_value,
                marketplace_head_image_id: currentMarketStack?.config?.marketplace_head_image_id,
                marketplace_compute_image_id: currentMarketStack?.config?.marketplace_compute_image_id || currentMarketStack?.config?.marketplace_head_image_id,
                prefix: inputValue,
                "os-type": selectedMarketStack ? selectedMarketStack.config?.os_types : form.info['os-type'],
				vncTokenAuthentication: isVncTokenAuthentication,
                vncRemoteClient: isVncRemoteClient,
                imageMode: imageMode
            },
            provider: 'gcp'
        }

        // Apply node image types and ensure accelerator fields are included
        // In unified mode, all nodes use the server's image settings
        updatedForm.data.node.group = updatedForm.data.node.group.map((node, index) => ({
            ...node,
            group_node_prefix: imageMode === IMAGE_MODE.UNIFIED 
                ? form.data.server.image_name 
                : node.group_node_prefix,
            image_type: imageMode === IMAGE_MODE.UNIFIED 
                ? serverImageType 
                : (nodeImageTypes[index] || IMAGE_TYPE.HEIDI_STACK),
            accelerator_type: node.accelerator_type || null,
            accelerator_count: (node.accelerator_count !== undefined && node.accelerator_count !== null) ? node.accelerator_count : 0
        }));

        // Ensure accelerator fields are included for server
        if (!updatedForm.data.server.accelerator_type) {
            updatedForm.data.server.accelerator_type = null;
        }
        if (!updatedForm.data.server.accelerator_count && updatedForm.data.server.accelerator_count !== 0) {
            updatedForm.data.server.accelerator_count = 0;
        }

        setForm(updatedForm);
        updatedForm.data.node.group.forEach(node => {
            if (node.group_node_prefix === '') node.group_node_prefix = updatedForm.info.prefix
        });

        // Add excluded global provision scripts (Feature 002 enhancement)
        // Always include this field (even if empty) to ensure consistent database state
        updatedForm.excludedGlobalScripts = excludedGlobalScripts || [];

        // Add cluster applications (Feature: cluster-apps)
        updatedForm.applications = clusterApplications || [];
        
        // Add owner selection if super-admin selected a different owner
        const saveData = { ...updatedForm };
        if (currentUser.role && currentUser.role.includes('super-admin') && selectedOwner) {
            saveData.ownerUsername = selectedOwner;
        }
        console.log('Saving cluster with data:', saveData);
        dispatch(Actions.saveCluster(saveData));
    };


    const startSave = () => handleSave({ confirmed: false });
    const startEdit = () => handleEdit({ confirmed: false });

    // Popover delay and close handlers
    //const delay = ms => new Promise(resolve => setTimeout(resolve, ms));
    const handlePopoverOpen = async event => {
        const targettemp = event.currentTarget;
        handlePopoverClose();
        if (popovertarget !== targettemp) {
            clearTimeout(timeoutid);
            const nullTimeout = setTimeout(() => {
                if (isMountedRef.current) setAnchorEl(null);
            }, 0);
            const targetTimeout = setTimeout(() => {
                if (isMountedRef.current) setAnchorEl(targettemp);
            }, 0);
            setPopovertarget(targettemp);
            const closeTimeout = setTimeout(() => {
                if (isMountedRef.current) handlePopoverClose();
            }, 4000);
            setTimeoutid(closeTimeout);
            
            // Store timeout IDs for cleanup
            return () => {
                clearTimeout(nullTimeout);
                clearTimeout(targetTimeout);
                clearTimeout(closeTimeout);
            };
        } else {
            clearTimeout(timeoutid);
            const targettemp = event.currentTarget;
            const targetTimeout = setTimeout(() => {
                if (isMountedRef.current) setAnchorEl(targettemp);
            }, 0);
            const closeTimeout = setTimeout(() => {
                if (isMountedRef.current) handlePopoverClose();
            }, 4000);
            setTimeoutid(closeTimeout);
            
            // Store timeout IDs for cleanup
            return () => {
                clearTimeout(targetTimeout);
                clearTimeout(closeTimeout);
            };
        }
    };
    const handlePopoverClose = () => {
        if (isMountedRef.current) {
            setAnchorEl(null);
            clearTimeout(timeoutid);
        }
    };
    const isODDCBurstingSelectionEnabled = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/moab/isIntegrated`)
            if (response.status !== 200) return false
            return response.data.isIntegrated
        } catch (error) {
            console.error('Error fetching from API:', error)
        }
    }
    const [moabIsIntegrated, setMoabIsIntegrated] = useState(null);
    useEffect(() => {
        const fetchDataAsync = async () => {
            const isIntegrated = await isODDCBurstingSelectionEnabled();
            setMoabIsIntegrated(isIntegrated);
        };
        fetchDataAsync();
    }, []);

    const [burstMode, setBurstMode] = useState(null)

    useEffect(() => {
        const initialValues = form?.data?.node?.group.map(group => group.group_node_prefix || '');
        setGroupNodeInputValues(initialValues);
    }, [form]);

    useEffect(() => {
        if (burstMode !== null) return
        if (props.type === 'new') {
            setBurstMode('off');
            return
        }
        if (form?.info?.bursting?.mode) setBurstMode(form.info.bursting.mode)
    }, [form, burstMode, currentMarketStack, props.type]);

    useEffect(() => {
        if (burstMode === null) return
        if (!form?.info) return
        form.info.bursting.mode = burstMode
    }, [form, burstMode]);

    const validateServerPrefix = (prefix) => { // Since "Node Prefix" field is not a "Formsy" input. This is how we need to show validations.
        const invalidPrefixCharacter = 'Invalid characters in the prefix. Use only lowercase letters, numbers, and hyphens.';
        const invalidPrefixHyphenUsage = 'Hyphens cannot start or end the prefix, and consecutive hyphens are not allowed.';
        if (/[^a-z0-9-]/g.test(prefix)) setPrefixError(invalidPrefixCharacter);
        else if (/^-|-$|--/g.test(prefix)) setPrefixError(invalidPrefixHyphenUsage);
        else setPrefixError('');
    }

    const validateNodePrefix = (prefix, index) => {
        const invalidPrefixCharacter = 'Invalid characters in the prefix. Use only lowercase letters, numbers, and hyphens.';
        const invalidPrefixHyphenUsage = 'Hyphens cannot start or end the prefix, and consecutive hyphens are not allowed.';
        let error = '';
        if (/[^a-z0-9-]/g.test(prefix)) error = invalidPrefixCharacter;
        else if (/^-|-$|--/g.test(prefix)) error = invalidPrefixHyphenUsage;

        setNodePrefixErrors(prevErrors => {
            const newErrors = [...prevErrors];
            newErrors[index] = error;
            return newErrors;
        });
    };

    // Download marketplace provision script if configured
    useEffect(() => {
        const downloadProvisionScript = async () => {
            if (
                currentMarketStack?.config?.provision_script && 
                form?.data?.provision &&
                !form.data.provision.script // Only download if no script already exists
            ) {
                dispatch(ActionsDialog.toggleLoading());
                try {
                    const scriptContent = await dispatch(MarketActions.getProvisionScript(currentMarketStack.config.provision_script));
                    setForm(_.set({ ...form }, 'data', { 
                        ...form.data, 
                        provision: { 
                            ...form.data.provision, 
                            script: scriptContent 
                        } 
                    }));
                } catch (error) {
                    console.error("Error downloading provision script:", error);
                } finally {
                    dispatch(ActionsDialog.toggleLoading());
                }
            }
        };
        
        downloadProvisionScript();
    }, [currentMarketStack, form, dispatch, setForm]);

    return (
        <React.Fragment>
            {form && (
                <React.Fragment>
                {/* Title & actions */}
                <DialogTitle className={clsx(classes.modalHeader, 'h-54 min-h-54 p-20')}>
                    <div className="flex flex-1 w-full items-center justify-between">
                        <div className="flex flex-col max-w-full">
                            <div className="flex items-center max-w-full">
                                <FuseAnimate animation="transition.expandIn" delay={300}>
                                    <img
                                        id="gcp_provider-logo"
                                        className="w-32 sm:w-48 mr-8 sm:mr-16 rounded"
                                        src={imageSrc}
                                        alt="Provider Logo"
                                    />
                                </FuseAnimate>
                                <div className="flex flex-col min-w-0">
                                    <FuseAnimate animation="transition.slideLeftIn" delay={300}>
                                        <Typography id="gcp_cluster-name-header" className="text-16 text-white sm:text-20 truncate">
                                            {title}
                                        </Typography>
                                    </FuseAnimate>
                                    <FuseAnimate animation="transition.slideLeftIn" delay={300}>
                                        <Typography id="gcp_provider-name-header" className="text-white" variant="caption">
                                            {"Google Cloud Platform"}
                                        </Typography>
                                    </FuseAnimate>
                                </div>
                            </div>
                        </div>
                        <div>
                            <Button
                                className="whitespace-no-wrap mr-2"
                                id="gcp_advanced-btn"
                                variant="contained"
                                disabled={loading}
                                onClick={() => setAdvanced(!advanced)}
                            >
                                {advanced ? 'Basic' : 'Advanced'}
                            </Button>
                            {props.type === 'new' && (
                                <Button
                                    className="whitespace-no-wrap mx-2"
                                    id="gcp_save-btn"
                                    disabled={!isFormValid || !areNonFormsyRequiredFieldsValid()}
                                    variant="contained"
                                    onClick={() => {
                                        startSave();
                                        dispatch(MarketActions.clearStack());
                                    }}
                                >
                                    {'Save'}
                                </Button>
                            )}
                            {props.type === 'edit' && form.info.state === CLUSTER_STATES.DESTROYED && (
                                <Button
                                    className="whitespace-no-wrap mx-2"
                                    id="gcp_save-btn"
                                    disabled={!isFormValid || loading || !form.data.server.image_name}
                                    variant="contained"
                                    onClick={() => {
                                        startEdit();
                                        dispatch(MarketActions.clearStack());
                                    }}
                                >
                                    update
                                </Button>
                            )}
                            <Button
                                className="whitespace-no-wrap ml-2"
                                id="gcp_close-btn"
                                variant="contained"
                                onClick={handleClose}
                            >
                                Close
                            </Button>
                        </div>
                    </div>
                </DialogTitle>

                {/* Quota warnings */}
                {(usedCpus > limitCpus || usedNetwork > limitNetwork || usedIntances > limitIntances) && (
                    <Box p={2} my={2} bgcolor="#FFF3E0" borderRadius={1}>
                        {usedCpus > limitCpus && (
                            <span className={classes.error2}>
                                <Icon>error</Icon> <span>The quota of CPUs established by the provider is exceeded</span>
                            </span>
                        )}
                        {usedNetwork > limitNetwork && (
                            <span className={classes.error2}>
                                <Icon>error</Icon> <span>The quota of network established by the provider is exceeded</span>
                            </span>
                        )}
                        {usedIntances > limitIntances && (
                            <span className={classes.error2}>
                                <Icon>error</Icon> <span>The quota of Intances established by the provider is exceeded</span>
                            </span>
                        )}
                    </Box>
                )}

                <DialogContent className={clsx(classes.content, 'p-16 -mr-200 ml-20')}>
                    <DisabledFormWrapper isDisabled={disableInputs}>
                        <Formsy ref={formRef} onValid={enableButton} onInvalid={disableButton}>
                            <OwnerSelectionCard
                                value={selectedOwner}
                                onChange={setSelectedOwner}
                                label="Cluster Owner"
                                title="Cluster Owner"
                                showInAdvancedMode={true}
                                advanced={advanced}
                                disabled={props.type === 'edit' && form && form.info && form.info.state !== CLUSTER_STATES.DESTROYED}
                            />
                            {/* Basic Information */}
                            <Card style={cardStyle}>
                                <CardHeader
                                    title={
                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                            <Box display="flex" alignItems="center">
                                                <Icon className="mr-8">info</Icon>
                                                <Typography style={sectionTitleStyle}>Basic Information</Typography>
                                            </Box>
                                            {props.type === 'edit' && form?.id && (
                                                <Typography style={{color: '#666', fontSize: '14px', fontWeight: 'normal'}}>
                                                    ID: {form.id}
                                                </Typography>
                                            )}
                                        </Box>
                                    }
                                    style={cardHeaderStyle}
                                />
                                <CardContent>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        {/* Left Column - fixed width */}
                                        <div style={{ width: '48%' }}>
                                            <TextFieldFormsy
                                                className={clsx(classes.input, 'mr-16')}
                                                style={{ marginBottom: '16px', width: '150%' }}
                                                id="gcp_name"
                                                name="name"
                                                label="Cluster Name"
                                                type="text"
                                                variant="outlined"
                                                value={form.name}
                                                onChange={handleChange}
                                                required
                                                placeholder="Your Cluster Name (No Spaces or Capitals)"
                                                validations={{
                                                    isNameUnique: (formValues, fieldValue) => {
                                                        if (clusterNames.includes(fieldValue) && !(props.type === 'edit')) {
                                                            return 'This name is already in use. Please choose a different one.';
                                                        }
                                                        return true;
                                                    },
                                                    isString: (formValues, fieldValue) => typeof fieldValue === 'string' || 'Name must be a string.',
                                                    doesNotExceedMaxLength: (formValues, fieldValue) =>
                                                        !fieldValue || fieldValue.length <= 15 || 'Name cannot exceed 15 characters.',
                                                    hasValidCharacters: (formValues, fieldValue) =>
                                                        !fieldValue || !/[^a-z0-9-]/g.test(fieldValue) || 'Invalid characters in the name. Use only lowercase letters, numbers, and hyphens.',
                                                    doesNotStartOrEndWithHyphen: (formValues, fieldValue) =>
                                                        !fieldValue || !/^-|-$|--/g.test(fieldValue) || 'Hyphens cannot start or end the name, and consecutive hyphens are not allowed.',
                                                    doesNotStartWithInteger: (formValues, fieldValue) =>
                                                        !fieldValue || !/^\d/.test(fieldValue) || 'Name cannot start with a number.',
                                                }}
                                                validationErrors={{
                                                    isNameUnique: 'This name is already in use. Please choose a different one.',
                                                    isString: 'Name must be a string.',
                                                    doesNotExceedMaxLength: 'Name cannot exceed 15 characters.',
                                                    hasValidCharacters: 'Invalid characters in the name. Use only lowercase letters, numbers, and hyphens.',
                                                    doesNotStartOrEndWithHyphen: 'Hyphens cannot start or end the name, and consecutive hyphens are not allowed.',
                                                    doesNotStartWithInteger: 'Name cannot start with a number.'
                                                }}
                                            />
                                            <SelectFormsy
                                                className={clsx(classes.input)}
                                                id="gcp_os-type"
                                                name="os-type"
                                                label="Operating System"
                                                type="select"
                                                variant="outlined"
                                                value={getDefaultOsType()}
                                                onChange={e => setInfoObj(e.target)}
                                                required
                                                disabled={marketplaceOSTypes && supportedOSs.filter(os => marketplaceOSTypes.includes(os)).length === 1}
                                                style={{ width: '400px' }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        style: { maxHeight: 300 }
                                                    }
                                                }}
                                            >
                                                {
                                                    supportedOSs
                                                        .filter(os => !marketplaceOSTypes || marketplaceOSTypes.includes(os))
                                                        .map((os, index) => (
                                                            <MenuItem id={`os-${os}`} key={index} value={os}>
                                                                {os}
                                                            </MenuItem>
                                                        ))}
                                            </SelectFormsy>
                                        </div>

                                        {/* Divider - centered */}
                                        <div style={{ 
                                            width: '2px', 
                                            backgroundColor: '#e0e0e0', 
                                            margin: '10px 0',
                                            alignSelf: 'stretch'
                                        }} />

                                        {/* Right Column - fixed width */}
                                        <div style={{ width: '48%' }}>
                                            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                                                <TextField
                                                    ref={textFieldRef}
                                                    label="Stack"
                                                    className={clsx(classes.input)}
                                                    style={{ flex: 1 }}
                                                    placeholder="Your Image Name or Select Stack"
                                                    required
                                                    disabled={Boolean(currentMarketStack)}
                                                    value={form.data.server.image_name || ''}
                                                    onChange={e => {
                                                        form.data.server.image_name = e.target.value
                                                        setForm({ ...form }) //done for rerender
                                                        validateServerPrefix(e.target.value)
                                                    }}
                                                    variant="outlined"
                                                    error={Boolean(prefixError)}
                                                    helperText={prefixError}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="start">
                                                                <IconButton disabled={Boolean(currentMarketStack)} onClick={handleMenuOpen}>
                                                                    <ArrowDropDownIcon />
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                                
                                                <SelectFormsy
                                                    className={clsx(classes.input)}
                                                    id="gcp_server_image_type"
                                                    name="server_image_type"
                                                    label="Image Type"
                                                    type="select"
                                                    variant="outlined"
                                                    value={serverImageType}
                                                    onChange={handleServerImageTypeChange}
                                                    style={{ width: '180px' }}
                                                    disabled={Boolean(currentMarketStack)}
                                                >
                                                    <MenuItem value={IMAGE_TYPE.HEIDI_STACK}>Heidi Stack</MenuItem>
                                                    <MenuItem value={IMAGE_TYPE.CUSTOM_STACK}>Custom Stack</MenuItem>
                                                </SelectFormsy>
                                            </div>
                                            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginTop: '16px' }}>
                                                <SelectFormsy
                                                    className={clsx(classes.input)}
                                                    id="gcp_image_mode"
                                                    name="image_mode"
                                                    label="Image Mode"
                                                    type="select"
                                                    variant="outlined"
                                                    value={imageMode}
                                                    onChange={e => setImageMode(e.target.value)}
                                                    style={{ width: '220px' }}
                                                    disabled={Boolean(currentMarketStack)}
                                                >
                                                    <MenuItem value={IMAGE_MODE.MULTI_IMAGE}>Multi-Image (Legacy)</MenuItem>
                                                    <MenuItem value={IMAGE_MODE.UNIFIED}>Unified (Single Image)</MenuItem>
                                                </SelectFormsy>
                                                <Typography variant="body2" color="textSecondary" style={{ marginTop: '8px', maxWidth: '300px' }}>
                                                    {imageMode === IMAGE_MODE.UNIFIED
                                                        ? 'Single unified image for head and compute nodes'
                                                        : 'Separate images for head node and compute nodes'}
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                            
                            <Menu
                                            anchorEl={textFieldRef.current}
                                            keepMounted
                                            open={Boolean(menuAnchorEl)}
                                            onClose={handleMenuClose}
                                            PaperProps={{
                                                style: {
                                                    width: textFieldRef.current ? textFieldRef.current.clientWidth : 'auto',
                                                    maxHeight: 200,
                                                    overflow: 'auto'
                                                }
                                            }}
                                        >
                                            {
                                                stacks
                                                    .filter(stack => stack.info?.state === "success" && stack?.provider === "gcp")
                                                    .length > 0
                                                    ? stacks
                                                        .filter(stack => stack.info?.state === "success" && stack?.provider === "gcp")
                                                        .map((stack, index) => (
                                                            <MenuItem
                                                                key={index}
                                                                onClick={() => {
                                                                    form.data.server.image_name = stack.data.prefix
                                                                    setForm({ ...form }) // done for rerender
                                                                    handleMenuClose()
                                                                }}
                                                            >
                                                                {stack.data.prefix}
                                                            </MenuItem>
                                                        ))
                                                    : <MenuItem disabled>No stack builds available for this provider</MenuItem>
                                            }
                                        </Menu>

                            {/* Two-column layout for Infrastructure and Resource Manager */}
                            <div style={columnContainerStyle}>
                                {/* Left Column - Infrastructure */}
                                <div style={columnStyle}>
                                    <Card style={cardStyle}>
                                        <CardHeader
                                            title={
                                                <Box display="flex" alignItems="center">
                                                    <Icon className="mr-8">cloud</Icon>
                                                    <Typography style={sectionTitleStyle}>Infrastructure</Typography>
                                                </Box>
                                            }
                                            action={
                                                /* Zone Availability Indicator - shows when zone is selected */
                                                form && form.data && form.data.network && form.data.network.zone_id && (
                                                    <ZoneAvailabilityIndicator
                                                        isLoading={isAvailabilityLoading}
                                                        isAvailable={unableToVerify ? null : isZoneAvailable(form.data.network.zone_id)}
                                                        missingInstances={getMissingInstances(form.data.network.zone_id)}
                                                        error={availabilityError}
                                                        unableToVerify={unableToVerify}
                                                    />
                                                )
                                            }
                                            style={cardHeaderStyle}
                                        />
                                        <CardContent>
                                            <div className="flex flex-row my-16 w-full">
                                                <SelectFormsy
                                                    className={clsx(classes.input, 'w-1/2 mr-8')}
                                                    id="gcp_credential_id"
                                                    name="credential_id"
                                                    label="Credential *"
                                                    type="select"
                                                    variant="outlined"
                                                    value={form.credential_id}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    required
                                                    MenuProps={{
                                                        PaperProps: {
                                                            style: { maxHeight: 300 }
                                                        }
                                                    }}
                                                >
                                                    {filteredCredentials.map((c, i) => (
                                                        <MenuItem id={c.name} key={i} value={c.id}>
                                                            {c.name}
                                                        </MenuItem>
                                                    ))}
                                                </SelectFormsy>
                                                <SelectFormsy
                                                    className={clsx(classes.input, 'w-1/2 ml-8')}
                                                    id="gcp_server-instance_id"
                                                    name="instance_id"
                                                    label="Head Node Size *"
                                                    type="select"
                                                    variant="outlined"
                                                    value={form.data.server.instance_id}
                                                    onChange={e => setServerObj(e.target)}
                                                    fullWidth
                                                    required
                                                    MenuProps={{
                                                        PaperProps: {
                                                            style: { maxHeight: 300 }
                                                        }
                                                    }}
                                                >
                                                    {
                                                        serverInstanceSizes
                                                            .filter(s => !marketplaceInstanceIds || marketplaceInstanceIds.includes(s.instance_id))
                                                            .map((s, i) => (
                                                                <MenuItem id={`server-${s.name}`} key={i} value={s.instance_id}>
                                                                    {s.name}
                                                                </MenuItem>
                                                            ))
                                                    }
                                                </SelectFormsy>
                                            </div>
                                            
                                            {/* Server Accelerator Configuration - only for n1- instances */}
                                            {form.data.server.instance_id && form.data.server.instance_id.startsWith('n1-') && (
                                                <div className="flex flex-row my-16 w-full gap-4">
                                                    <SelectFormsy
                                                        className={clsx(classes.input, 'w-1/2 mr-8')}
                                                        id="gcp_server_accelerator_type"
                                                        name="accelerator_type"
                                                        label="Head Node Accelerator Type"
                                                        type="select"
                                                        variant="outlined"
                                                        value={form.data.server.accelerator_type || ''}
                                                        onChange={setServerAcceleratorType}
                                                        fullWidth
                                                        MenuProps={{
                                                            PaperProps: {
                                                                style: { maxHeight: 300 }
                                                            }
                                                        }}
                                                    >
                                                        <MenuItem id="gcp_no_accelerator" value={ACCELERATOR_TYPES.NONE}>
                                                            {ACCELERATOR_LABELS[ACCELERATOR_TYPES.NONE]}
                                                        </MenuItem>
                                                        <MenuItem id="gcp_k80" value={ACCELERATOR_TYPES.NVIDIA_K80}>
                                                            {ACCELERATOR_LABELS[ACCELERATOR_TYPES.NVIDIA_K80]}
                                                        </MenuItem>
                                                        <MenuItem id="gcp_p4" value={ACCELERATOR_TYPES.NVIDIA_P4}>
                                                            {ACCELERATOR_LABELS[ACCELERATOR_TYPES.NVIDIA_P4]}
                                                        </MenuItem>
                                                        <MenuItem id="gcp_p100" value={ACCELERATOR_TYPES.NVIDIA_P100}>
                                                            {ACCELERATOR_LABELS[ACCELERATOR_TYPES.NVIDIA_P100]}
                                                        </MenuItem>
                                                        <MenuItem id="gcp_v100" value={ACCELERATOR_TYPES.NVIDIA_V100}>
                                                            {ACCELERATOR_LABELS[ACCELERATOR_TYPES.NVIDIA_V100]}
                                                        </MenuItem>
                                                        <MenuItem id="gcp_t4" value={ACCELERATOR_TYPES.NVIDIA_T4}>
                                                            {ACCELERATOR_LABELS[ACCELERATOR_TYPES.NVIDIA_T4]}
                                                        </MenuItem>
                                                        <MenuItem id="gcp_a100" value={ACCELERATOR_TYPES.NVIDIA_A100}>
                                                            {ACCELERATOR_LABELS[ACCELERATOR_TYPES.NVIDIA_A100]}
                                                        </MenuItem>
                                                    </SelectFormsy>
                                                    <TextField
                                                        className={clsx(classes.input, 'w-1/2 ml-8')}
                                                        id="gcp_server_accelerator_count"
                                                        name="accelerator_count"
                                                        label="Count"
                                                        type="number"
                                                        variant="outlined"
                                                        value={form.data.server.accelerator_count || 0}
                                                        onChange={e => setServerObj({ name: 'accelerator_count', value: parseInt(e.target.value) || 0 })}
                                                        fullWidth
                                                        disabled={!form.data.server.accelerator_type}
                                                        inputProps={{ min: 0, max: 8 }}
                                                    />
                                                </div>
                                            )}
                                            
                                            <div className="flex flex-row my-16 w-full">
                                                <SelectFormsy
                                                    className={clsx(classes.input, 'w-full mr-8')}
                                                    id="gcp_region_id"
                                                    name="region_id"
                                                    label="Region *"
                                                    type="select"
                                                    variant="outlined"
                                                    value={form.data.network.region_id}
                                                    onChange={e => setNetworkObj(e.target)}
                                                    fullWidth
                                                    required
                                                    MenuProps={{
                                                        PaperProps: {
                                                            style: { maxHeight: 300 }
                                                        }
                                                    }}
                                                >
                                                    {
                                                        regions
                                                            .map((region, index) => (
                                                                <MenuItem id={region.name} key={index} value={region.region_id}>
                                                                    {region.name}
                                                                </MenuItem>
                                                            ))}
                                                </SelectFormsy>
                                                <SelectFormsy
                                                    className={clsx(classes.input, 'w-full ml-8')}
                                                    disabled={zones.length === 0}
                                                    id="gcp_zone_id"
                                                    name="zone_id"
                                                    label={getZoneLabel(form.data.network.region_id, form.data.network.subnet_id, form.data.network.vpc_id)}
                                                    type="select"
                                                    variant="outlined"
                                                    value={form.data.network.zone_id || ''}
                                                    onChange={e => setNetworkObj(e.target)}
                                                    fullWidth
                                                    required={
                                                        !form.data.network.subnet_id &&
                                                        !form.data.network.vpc_id &&
                                                        zones.length !== 0
                                                    }
                                                    MenuProps={{
                                                        PaperProps: {
                                                            style: { maxHeight: 300 }
                                                        }
                                                    }}
                                                >
                                                    <MenuItem id="gcp_none" value="">
                                                        None
                                                    </MenuItem>
                                                    {
                                                        zones
                                                            .map((z, i) => (
                                                                <MenuItem id={z.name} key={i} value={z.zone_id}>
                                                                    {z.name}
                                                                </MenuItem>
                                                            ))}
                                                </SelectFormsy>
                                            </div>
                                            
                                            {/* Spot VM Toggle - only shown in advanced mode */}
                                            {advanced && (
                                                <Box mt={2} display="flex" alignItems="center">
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={spotVmEnabled}
                                                                onChange={(e) => setSpotVmEnabled(e.target.checked)}
                                                                name="spotVm"
                                                                color="primary"
                                                            />
                                                        }
                                                        label={
                                                            <Box display="flex" alignItems="center">
                                                                <Typography variant="body2">Enable Spot/Preemptible Instances</Typography>
                                                                <HelpDialog 
                                                                    title="Spot/Preemptible Instances"
                                                                    content="Spot instances (also known as preemptible VMs in Google Cloud) offer significant cost savings compared to regular instances, sometimes up to 80% less expensive.

These instances use excess compute capacity in Google Cloud data centers and can be terminated (preempted) by Google with little notice when the capacity is needed elsewhere."                                                                >
                                                                    <Icon style={{ marginLeft: '8px', color: '#757575', fontSize: '18px' }}>help_outline</Icon>
                                                                </HelpDialog>
                                                            </Box>
                                                        }
                                                    />
                                                </Box>
                                            )}
                                        </CardContent>
                                    </Card>
                                </div>

                                {/* Right Column - Resource Manager */}
                                <div style={columnStyle}>
                                    <Card style={cardStyle}>
                                        <CardHeader
                                            title={
                                                <Box display="flex" alignItems="center">
                                                    <Icon className="mr-8">settings_applications</Icon>
                                                    <Typography style={sectionTitleStyle}>Resource Manager Configuration</Typography>
                                                </Box>
                                            }
                                            style={cardHeaderStyle}
                                        />
                                        <CardContent>
                                            <div className="flex flex-row my-16 w-full gap-4">
                                                <SelectFormsy
                                                    className={clsx(classes.input, "flex-1 mr-8")}
                                                    id="gcp_manager"
                                                    name="manager"
                                                    label="Manager"
                                                    type="select"
                                                    variant="outlined"
                                                    value={form.data.manager}
                                                    onChange={e => setDataObj(e.target)}
                                                    required
                                                    MenuProps={{
                                                        PaperProps: {
                                                            style: { maxHeight: 200 }
                                                        }
                                                    }}
                                                >
                                                    <MenuItem id="gcp_none" value="none">
                                                        None
                                                    </MenuItem>
                                                    <MenuItem id="gcp_torque" value="torque">
                                                        Torque
                                                    </MenuItem>
                                                    <MenuItem id="gcp_slurm" value="slurm">
                                                        Slurm
                                                    </MenuItem>
                                                </SelectFormsy>
                                                <TextField
                                                    className={clsx(classes.input, "flex-1 ml-8")}
                                                    id="gcp_hostname"
                                                    name="hostname"
                                                    label="Host Name"
                                                    type="text"
                                                    variant="outlined"
                                                    value={form.data.moab.hostname || ''}
                                                    fullWidth
                                                    required={form.data.manager === RESOURCE_MANAGER.TORQUE}
                                                    disabled={form.data.manager !== RESOURCE_MANAGER.TORQUE}
                                                    placeholder="Host Name"
                                                    onChange={e => setClusterInfo(e)}
                                                    InputProps={{
                                                        readOnly: form.data.manager !== RESOURCE_MANAGER.TORQUE,
                                                        classes: {
                                                            root: form.data.manager !== RESOURCE_MANAGER.TORQUE ? classes.disabledInput : '',
                                                            notchedOutline: form.data.manager !== RESOURCE_MANAGER.TORQUE ? classes.disabledOutline : '',
                                                        },
                                                        style: {
                                                            // Prevent any click interactions when disabled
                                                            pointerEvents: form.data.manager !== RESOURCE_MANAGER.TORQUE ? 'none' : 'auto'
                                                        }
                                                    }}
                                                    InputLabelProps={{
                                                        classes: {
                                                            root: form.data.manager !== RESOURCE_MANAGER.TORQUE ? classes.disabledLabel : ''
                                                        }
                                                    }}
                                                    inputProps={{
                                                        style: {
                                                            color: form.data.manager !== RESOURCE_MANAGER.TORQUE ? 'rgba(0, 0, 0, 0.38)' : 'inherit'
                                                        }
                                                    }}
                                                />
                                                <TextField
                                                    className={clsx(classes.input, "flex-1 ml-8")}
                                                    id="gcp_ip"
                                                    name="ip"
                                                    label={form.data.server.isPrivate ? 'Private IP' : 'Public IP'}
                                                    type="text"
                                                    variant="outlined"
                                                    value={form.data.moab.ip || ''}
                                                    fullWidth
                                                    required={form.data.manager === RESOURCE_MANAGER.TORQUE}
                                                    disabled={form.data.manager !== RESOURCE_MANAGER.TORQUE}
                                                    placeholder={form.data.server.isPrivate ? 'Private IP' : 'Public IP'}
                                                    onChange={e => setClusterInfo(e)}
                                                    InputProps={{
                                                        readOnly: form.data.manager !== RESOURCE_MANAGER.TORQUE,
                                                        classes: {
                                                            root: form.data.manager !== RESOURCE_MANAGER.TORQUE ? classes.disabledInput : '',
                                                            notchedOutline: form.data.manager !== RESOURCE_MANAGER.TORQUE ? classes.disabledOutline : '',
                                                        },
                                                        style: {
                                                            // Prevent any click interactions when disabled
                                                            pointerEvents: form.data.manager !== RESOURCE_MANAGER.TORQUE ? 'none' : 'auto'
                                                        }
                                                    }}
                                                    InputLabelProps={{
                                                        classes: {
                                                            root: form.data.manager !== RESOURCE_MANAGER.TORQUE ? classes.disabledLabel : ''
                                                        }
                                                    }}
                                                    inputProps={{
                                                        style: {
                                                            color: form.data.manager !== RESOURCE_MANAGER.TORQUE ? 'rgba(0, 0, 0, 0.38)' : 'inherit'
                                                        }
                                                    }}
                                                    error={Boolean(form.data.manager === RESOURCE_MANAGER.TORQUE && form.data.moab.ip && !ipValid(form.data.moab.ip))}
                                                    helperText={form.data.manager === RESOURCE_MANAGER.TORQUE && form.data.moab.ip && !ipValid(form.data.moab.ip) ? 'Not a valid IP' : ''}
                                                />
                                            </div>
                                            {moabIsIntegrated && !currentMarketStack && (
                                                <Box mt={2} sx={form.data.manager !== RESOURCE_MANAGER.TORQUE ? { opacity: 0.6 } : {}}>
                                                    <Typography variant="subtitle2" style={{marginBottom: '8px'}}>Bursting Configuration:</Typography>
                                                    <FormControl component="fieldset" disabled={form.data.manager !== RESOURCE_MANAGER.TORQUE}>
                                                        <RadioGroup
                                                            row
                                                            id="gcp_burstOnDeploy"
                                                            name="burstOnDeploy"
                                                            value={burstMode}
                                                            onChange={e => {
                                                                if (form.data.manager === RESOURCE_MANAGER.TORQUE) {
                                                                    const mode = e.target.value;
                                                                    setBurstMode(mode);
                                                                }
                                                            }}
                                                        >
                                                            <FormControlLabel 
                                                                value="off" 
                                                                id="gcp_radio-off" 
                                                                control={<Radio color="primary" />} 
                                                                label="Off" 
                                                            />
                                                            <FormControlLabel 
                                                                value="min" 
                                                                id="gcp_radio-min" 
                                                                control={<Radio color="primary" />} 
                                                                label="Min" 
                                                            />
                                                            <FormControlLabel 
                                                                value="max" 
                                                                id="gcp_radio-max" 
                                                                control={<Radio color="primary" />} 
                                                                label="Max" 
                                                            />
                                                            <FormControlLabel 
                                                                value="all" 
                                                                id="gcp_radio-all" 
                                                                control={<Radio color="primary" />} 
                                                                label="All" 
                                                            />
                                                        </RadioGroup>
                                                    </FormControl>
                                                </Box>
                                            )}
                                        </CardContent>
                                    </Card>
                                </div>
                            </div>

                            {/* Advanced: VNC Settings & Provisioning Script in two columns */}
                            {advanced && (
                                <div style={columnContainerStyle}>
                                    {/* Left Column - VNC Settings */}
                                    <div style={columnStyle}>
                                        <Card style={{...cardStyle, height: '100%', display: 'flex', flexDirection: 'column'}}>
                                            <CardHeader
                                                title={
                                                    <Box display="flex" alignItems="center">
                                                        <Icon className="mr-8">desktop_windows</Icon>
                                                        <Typography style={sectionTitleStyle}>VNC Settings</Typography>
                                                    </Box>
                                                }
                                                style={cardHeaderStyle}
                                            />
                                            <CardContent style={{flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                                                <div className="flex flex-row items-center justify-start">
                                                    <div className="flex flex-row items-center justify-start w-1/2 mr-8">
                                                        VNC Token Authentication:
                                                        <Checkbox
                                                            checked={isVncTokenAuthentication}
                                                            onChange={event => {
                                                                const newValue = event.target.checked; 
                                                                setIsVncTokenAuthentication(newValue); 
                                                                setForm(prevForm => ({
                                                                    ...prevForm,
                                                                    info: {
                                                                        ...prevForm.info,
                                                                        vncTokenAuthentication: newValue 
                                                                    }
                                                                }));
                                                            }}
                                                            name="vncTokenAuthentication"
                                                            color="primary"
                                                        />
                                                    </div>
                                                    <div className="flex flex-row items-center justify-start w-1/2 ml-8">
                                                        VNC Remote Client:
                                                        <Checkbox
                                                            checked={isVncRemoteClient}
                                                            onChange={event => {
                                                                const newValue = event.target.checked;
                                                                setIsVncRemoteClient(newValue);
                                                                setForm(prevForm => ({
                                                                    ...prevForm,
                                                                    info: {
                                                                        ...prevForm.info,
                                                                        vncRemoteClient: newValue
                                                                    }
                                                                }));
                                                            }}
                                                            name="vncRemoteClient"
                                                            color="primary"
                                                        />
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </div>
                                    {/* Right Column - Provisioning Script (hidden for marketplace apps with hide_provision_script) */}
                                    {!marketplaceHideProvisionScript && (
                                    <div style={columnStyle}>
                                        <Card style={{...cardStyle, height: '100%', display: 'flex', flexDirection: 'column'}}>
                                            <CardHeader
                                                title={
                                                    <Box display="flex" alignItems="center">
                                                        <Icon className="mr-8">code</Icon>
                                                        <Typography style={sectionTitleStyle}>Provisioning Script</Typography>
                                                    </Box>
                                                }
                                                style={cardHeaderStyle}
                                            />
                                            <CardContent style={{flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                                                <div className="flex flex-row items-center justify-between">
                                                    <div className="flex items-center" style={{ position: 'relative' }}>
                                                        <input
                                                            className={classes.uploadInput}
                                                            onChange={e => handleUpload(e)}
                                                            id="gcp_upload-btn"
                                                            type="file"
                                                            disabled={disableScriptModification}
                                                            style={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                opacity: 0,
                                                                width: '100%',
                                                                height: '100%',
                                                                cursor: disableScriptModification ? 'not-allowed' : 'pointer',
                                                                zIndex: 1
                                                            }}
                                                        />
                                                        <label htmlFor="gcp_upload-btn">
                                                            <Button
                                                                id="gcp_upload-label"
                                                                variant="contained" 
                                                                size="small" 
                                                                className={classes.grayButton}
                                                                startIcon={<Icon>cloud_upload</Icon>}
                                                                component="span"
                                                                disabled={disableScriptModification}
                                                            >
                                                                Upload Script
                                                            </Button>
                                                        </label>
                                                    </div>
                                                    {!form?.data?.provision?.script ? (
                                                        <Typography variant="body2" color="textSecondary">
                                                            No Script
                                                        </Typography>
                                                    ) : (
                                                        <div className="flex items-center">
                                                            <Chip 
                                                                label="Script Uploaded" 
                                                                color="primary" 
                                                                size="small"
                                                                style={{marginRight: '8px'}}
                                                            />
                                                            {!disableScriptModification && (
                                                                <IconButton size="small" onClick={handleDeleteScript}>
                                                                    <Icon fontSize="small">delete</Icon>
                                                                </IconButton>
                                                            )}
                                                            <IconButton 
                                                                id="script-actions-view-button"
                                                                size="small" 
                                                                onClick={() => setScriptOpen(true)}
                                                            >
                                                                <Icon fontSize="small">visibility</Icon>
                                                            </IconButton>
                                                        </div>
                                                    )}
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </div>
                                    )}
                                </div>
                            )}

                            {/* Global Provision Scripts Preview - Feature 002 Enhancement */}
                            {/* Always render when disable_global_scripts is set (so configs load for auto-exclude), but hide the UI */}
                            {(advanced || marketplaceDisableGlobalScripts) && (
                                <div style={marketplaceDisableGlobalScripts ? { display: 'none' } : undefined}>
                                    <GlobalProvisionScriptsPreview
                                        provider="gcp"
                                        osType={form?.info?.['os-type'] || ''}
                                        clusterName={form?.name || ''}
                                        clusterId={form?.id}
                                        excludedScripts={excludedGlobalScripts}
                                        onExcludedScriptsChange={setExcludedGlobalScripts}
                                        disabled={disableInputs || marketplaceDisableGlobalScripts}
                                    />
                                </div>
                            )}

                            {/* Cluster Applications - visible in both Basic and Advanced modes when apps exist */}
                            {(advanced || clusterApplications.length > 0) && (
                                <ClusterApplicationsSection
                                    applications={clusterApplications}
                                    onChange={setClusterApplications}
                                    disabled={disableInputs}
                                    cardStyle={cardStyle}
                                    cardHeaderStyle={cardHeaderStyle}
                                    sectionTitleStyle={sectionTitleStyle}
                                />
                            )}

                            {/* Advanced: Capacity Reservations */}
                            {advanced && (
                                <Card style={cardStyle}>
                                    <CardHeader
                                        title={
                                            <Box display="flex" alignItems="center">
                                                <Icon style={{marginRight: '8px', color: '#2196F3'}}>how_to_reg</Icon>
                                                <Typography style={sectionTitleStyle}>Capacity Reservations</Typography>
                                            </Box>
                                        }
                                        style={cardHeaderStyle}
                                    />
                                    <CardContent>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={enableCapacityReservation}
                                                    onChange={event => {
                                                        const newValue = event.target.checked;
                                                        setEnableCapacityReservation(newValue);
                                                        setForm(prevForm => ({
                                                            ...prevForm,
                                                            data: {
                                                                ...prevForm.data,
                                                                network: {
                                                                    ...prevForm.data.network,
                                                                    enable_capacity_reservation: newValue
                                                                }
                                                            }
                                                        }));
                                                    }}
                                                    name="enableCapacityReservation"
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Box>
                                                    <Typography variant="body1" style={{fontWeight: 500}}>
                                                        Reserve Capacity Before Deploy
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary" style={{display: 'block', marginTop: '4px'}}>
                                                        Validate GPU and instance availability before deployment. Prevents partial cluster failures. Reservations auto-expire after 10 minutes.
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </CardContent>
                                </Card>
                            )}

                            {/* Storage Settings */}
                            {advanced && (
                                <Card style={cardStyle}>
                                    <CardHeader
                                        title={
                                            <Box display="flex" alignItems="center">
                                                <Icon className="mr-8">storage</Icon>
                                                <Typography style={sectionTitleStyle}>Storage Settings</Typography>
                                            </Box>
                                        }
                                        style={cardHeaderStyle}
                                    />
                                    <CardContent>
                                        <div className="flex flex-row my-16 w-full">
                                            <TextFieldFormsy
                                                className={clsx(classes.input, 'w-1/2 mr-8')}
                                                id="gcp_server-volume_size"
                                                name="volume_size"
                                                label="Server Volume (GB)"
                                                type="text"
                                                variant="outlined"
                                                value={form.data.server.volume_size}
                                                onChange={e => setServerObj(e.target)}
                                                fullWidth
                                                placeholder={`Disk size of the Cluster Head Node (Min: ${providerVolsize.min}, Max: ${providerVolsize.max})`}
                                                validations={{
                                                    mustbenumber: (formValues, fieldValue) => {
                                                        return validationInput(
                                                            'clusterservervolume',
                                                            formValues.instance_id,
                                                            fieldValue,
                                                            1,
                                                            "gcp",
                                                            currentMarketStack
                                                        );
                                                    }
                                                }}
                                                validationErrors={{
                                                    mustbenumber: validationMess('clusterservervolume', form.instance_id, 1, "gcp", currentMarketStack)
                                                }}
                                            />
                                            <TextFieldFormsy
                                                className={clsx(classes.input, 'mr-8')}
                                                id="gcp_node-volume_size"
                                                name="volume_size"
                                                label="Node Volume (GB)"
                                                type="text"
                                                variant="outlined"
                                                value={form.data.node.volume_size}
                                                onChange={e => setNodeObj(e.target)}
                                                fullWidth
                                                placeholder={`Disk size of the Cluster Nodes (Min: ${providerVolsize.min}, Max: ${providerVolsize.max})`}
                                                validations={{
                                                    mustbenumber: (formValues, fieldValue) => {
                                                        return validationInput('clusternodevolume', formValues.instance_id, fieldValue, 1, "gcp", currentMarketStack);
                                                    }
                                                }}
                                                validationErrors={{
                                                    mustbenumber: validationMess('clusternodevolume', form.instance_id, 1, "gcp", currentMarketStack)
                                                }}
                                            />
                                            <TextFieldFormsy
                                                className={clsx(classes.input)}
                                                id="gcp_idle_purge_s"
                                                name="idle_purge_s"
                                                label="Idle Purge Time (S)"
                                                type="number"
                                                variant="outlined"
                                                value={form.data.node.idle_purge_s}
                                                onChange={e => setNodeObj(e.target)}
                                                fullWidth
                                                inputProps={{
                                                    min: 0
                                                }}
                                            />
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Compute Nodes */}
                            <Card style={cardStyle}>
                                <CardHeader
                                    title={
                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                            <Box display="flex" alignItems="center">
                                                <Icon className="mr-8">device_hub</Icon>
                                                <Typography style={sectionTitleStyle}>Compute Nodes</Typography>
                                                <HelpDialog 
                                                    title="Compute Nodes Information"
                                                    content="Compute nodes are the worker machines in your Google Cloud Platform cluster."
                                                >
                                                    <Icon style={tooltipIconStyle}>help_outline</Icon>
                                                </HelpDialog>
                                            </Box>
                                            <Button
                                                variant="contained" size="small" startIcon={<Icon>add</Icon>}
                                                onClick={addComputeNode}
                                                disabled={form.data.node.group.length >= 250}
                                            >
                                                Add Node Group
                                            </Button>
                                        </Box>
                                    }
                                    style={cardHeaderStyle}
                                />
                                <CardContent>
                                    {nodeInstanceSizes && form.data.node.group.map((val, i) => (
                                        <Paper
                                            key={`node-${i}`}
                                            elevation={1}
                                            style={{
                                                marginBottom: '16px',
                                                padding: '16px',
                                                borderLeft: '4px solid #2196F3'
                                            }}
                                        >
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                                <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                                                    {val.group_node_prefix_label || `Node Group ${i + 1}`}
                                                </Typography>

                                                <IconButton
                                                    size="small"
                                                    color="default"
                                                    onClick={() => removeComputeNode(i)}
                                                    disabled={form.data.node.group.length <= 1}
                                                    style={{
                                                        color: form.data.node.group.length <= 1 ? 'rgba(0, 0, 0, 0.26)' : '#9e9e9e',
                                                        backgroundColor: 'transparent'
                                                    }}
                                                    disableRipple
                                                    aria-label="remove node group"
                                                >
                                                    <Icon>delete</Icon>
                                                </IconButton>
                                            </Box>
                                            
                                            <div style={{...inputGroupStyle, alignItems: 'center'}}>
                                                {/* Hidden input for group_node_prefix_label - not displayed */}
                                                <input
                                                    type="hidden"
                                                    name={`group_node_prefix_label_${i}`}
                                                    value={val.group_node_prefix_label || `Node Group ${i + 1}`}
                                                />
                                                {imageMode === IMAGE_MODE.MULTI_IMAGE && (
                                                    <>
                                                        <TextField
                                                            ref={el => groupNodePrefixRefs.current[i] = el}
                                                            className={clsx(classes.input)}
                                                            style={{flex: 1}}
                                                            label="Prefix"
                                                            value={groupNodeInputValues[i] || ''}
                                                            onChange={(e) => {
                                                                const newValue = e.target.value;
                                                                setGroupNodeInputValues(prevValues => ({
                                                                    ...prevValues,
                                                                    [i]: newValue
                                                                }));
                                                                setNodePrefix(i, newValue);
                                                                validateNodePrefix(newValue, i);
                                                            }} variant="outlined"
                                                            required
                                                            disabled={Boolean(currentMarketStack)}
                                                            error={!!prefixError}
                                                            helperText={nodePrefixErrors[i]}
                                                            InputProps={{
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <IconButton
                                                                            disabled={Boolean(currentMarketStack)}
                                                                            onClick={() => handleGroupNodeMenuOpen(i)}>
                                                                            <ArrowDropDownIcon />
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                        <Menu
                                                            anchorEl={groupNodePrefixRefs.current[activeMenuIndex]}
                                                            open={activeMenuIndex === i}
                                                            onClose={() => setActiveMenuIndex(null)}
                                                        >
                                                            {
                                                                stacks
                                                                    .filter(stack => stack.info?.state === "success" && stack?.provider === "gcp")
                                                                    .length > 0
                                                                    ? stacks
                                                                        .filter(stack => stack.info?.state === "success" && stack?.provider === "gcp")
                                                                        .map((stack, index) => (
                                                                            <MenuItem
                                                                                key={index}
                                                                                onClick={() => handleGroupNodeMenuItemClick(i, stack.data.prefix)}
                                                                            >
                                                                                {stack.data.prefix}
                                                                            </MenuItem>
                                                                        ))
                                                                    : <MenuItem disabled>No stack builds available for this provider</MenuItem>
                                                            }
                                                        </Menu>
                                                    </>
                                                )}
                                                <SelectFormsy
                                                    className={clsx(classes.input)}
                                                    style={{ flex: 2 }}
                                                    id={`node-size-${i}`}
                                                    name="node_instance_id"
                                                    label="Size *"
                                                    type="select"
                                                    variant="outlined"
                                                    value={val.instance_id}
                                                    onChange={e => setInstanceId(i, e.target.value)}
                                                    required
                                                    MenuProps={{
                                                        PaperProps: {
                                                            style: { maxHeight: 300 }
                                                        }
                                                    }}
                                                >
                                                    {nodeInstanceSizes
                                                        .filter(s => !marketplaceComputeIds || marketplaceComputeIds.includes(s.instance_id))
                                                        .map((s, i) => (
                                                            <MenuItem id={s.name} key={i} value={s.instance_id} disabled={s.instance_id === 'BM.GPU4.8' && selectedShapes.includes(s.instance_id)}>
                                                                {s.name}
                                                            </MenuItem>
                                                        ))}
                                                </SelectFormsy>
                                                
                                                {imageMode === IMAGE_MODE.MULTI_IMAGE && (
                                                    <SelectFormsy
                                                        className={clsx(classes.input)}
                                                        style={{ width: '180px' }}
                                                        id={`gcp_node_image_type_${i}`}
                                                        name={`node_image_type_${i}`}
                                                        label="Image Type"
                                                        type="select"
                                                        variant="outlined"
                                                        value={nodeImageTypes[i] || IMAGE_TYPE.HEIDI_STACK}
                                                        onChange={e => setNodeImageType(i, e.target.value)}
                                                        disabled={Boolean(currentMarketStack)}
                                                    >
                                                        <MenuItem value={IMAGE_TYPE.HEIDI_STACK}>Heidi Stack</MenuItem>
                                                        <MenuItem value={IMAGE_TYPE.CUSTOM_STACK}>Custom Stack</MenuItem>
                                                    </SelectFormsy>
                                                )}

                                                <TextFieldFormsy
                                                    className={clsx(classes.input)}
                                                    style={{width: '120px'}}
                                                    id={`count-${i}`}
                                                    name="count"
                                                    label="Count"
                                                    type="number"
                                                    variant="outlined"
                                                    value={val.count || "0"}
                                                    onChange={e => setInstanceCount(i, e.target, val.instance_id)}
                                                    required
                                                    inputProps={{
                                                        min: 0,
                                                        max: 250
                                                    }}
                                                />
                                            </div>
                                            
                                            {/* Node Accelerator Configuration - only for n1- instances */}
                                            {val.instance_id && val.instance_id.startsWith('n1-') && (
                                            <div style={{...inputGroupStyle, alignItems: 'center', marginTop: '16px'}}>
                                                    <SelectFormsy
                                                        className={clsx(classes.input)}
                                                        style={{ flex: 1 }}
                                                        id={`node_accelerator_type_${i}`}
                                                        name="accelerator_type"
                                                        label={val.accelerator_type ? "Accelerator Type" : ""}
                                                        type="select"
                                                        variant="outlined"
                                                        value={val.accelerator_type || ''}
                                                        onChange={e => {
                                                            const newValue = e.target.value;
                                                            setForm(prevForm => {
                                                                const updatedGroups = prevForm.data.node.group.map((group, idx) => 
                                                                    idx === i ? {
                                                                        ...group,
                                                                        accelerator_type: newValue || null,
                                                                        accelerator_count: newValue ? (group.accelerator_count || 1) : 0
                                                                    } : group
                                                                );
                                                                const groupFiltered = combineSameConfigurationNodes(updatedGroups);
                                                                return _.set({ ...prevForm }, 'data', { ...prevForm.data, node: { ...prevForm.data.node, group: groupFiltered } });
                                                            });
                                                        }}
                                                        displayEmpty
                                                        renderValue={(selected) => {
                                                            if (!selected || selected === '') {
                                                                return <span style={{ color: '#999' }}>Select Accelerator (Optional)</span>;
                                                            }
                                                            return ACCELERATOR_LABELS[selected] || selected;
                                                        }}
                                                        MenuProps={{
                                                            PaperProps: {
                                                                style: { maxHeight: 300 }
                                                            }
                                                        }}
                                                    >
                                                        <MenuItem id={`node_no_accelerator_${i}`} value={ACCELERATOR_TYPES.NONE}>
                                                            {ACCELERATOR_LABELS[ACCELERATOR_TYPES.NONE]}
                                                        </MenuItem>
                                                        <MenuItem id={`node_k80_${i}`} value={ACCELERATOR_TYPES.NVIDIA_K80}>
                                                            {ACCELERATOR_LABELS[ACCELERATOR_TYPES.NVIDIA_K80]}
                                                        </MenuItem>
                                                        <MenuItem id={`node_p4_${i}`} value={ACCELERATOR_TYPES.NVIDIA_P4}>
                                                            {ACCELERATOR_LABELS[ACCELERATOR_TYPES.NVIDIA_P4]}
                                                        </MenuItem>
                                                        <MenuItem id={`node_p100_${i}`} value={ACCELERATOR_TYPES.NVIDIA_P100}>
                                                            {ACCELERATOR_LABELS[ACCELERATOR_TYPES.NVIDIA_P100]}
                                                        </MenuItem>
                                                        <MenuItem id={`node_v100_${i}`} value={ACCELERATOR_TYPES.NVIDIA_V100}>
                                                            {ACCELERATOR_LABELS[ACCELERATOR_TYPES.NVIDIA_V100]}
                                                        </MenuItem>
                                                        <MenuItem id={`node_t4_${i}`} value={ACCELERATOR_TYPES.NVIDIA_T4}>
                                                            {ACCELERATOR_LABELS[ACCELERATOR_TYPES.NVIDIA_T4]}
                                                        </MenuItem>
                                                        <MenuItem id={`node_a100_${i}`} value={ACCELERATOR_TYPES.NVIDIA_A100}>
                                                            {ACCELERATOR_LABELS[ACCELERATOR_TYPES.NVIDIA_A100]}
                                                        </MenuItem>
                                                    </SelectFormsy>
                                                    <TextField
                                                        className={clsx(classes.input)}
                                                        style={{ width: '120px', flexShrink: 0 }}
                                                        id={`node_accelerator_count_${i}`}
                                                        name="accelerator_count"
                                                        label="Count"
                                                        type="number"
                                                        variant="outlined"
                                                        value={val.accelerator_count || 0}
                                                        onChange={e => {
                                                            const newValue = parseInt(e.target.value) || 0;
                                                            setForm(prevForm => {
                                                                const updatedGroups = prevForm.data.node.group.map((group, idx) => 
                                                                    idx === i ? {
                                                                        ...group,
                                                                        accelerator_count: newValue
                                                                    } : group
                                                                );
                                                                const groupFiltered = combineSameConfigurationNodes(updatedGroups);
                                                                return _.set({ ...prevForm }, 'data', { ...prevForm.data, node: { ...prevForm.data.node, group: groupFiltered } });
                                                            });
                                                        }}
                                                        disabled={!val.accelerator_type}
                                                        inputProps={{ min: 0, max: 8 }}
                                                    />
                                                </div>
                                            )}
                                        </Paper>
                                    ))}
                                </CardContent>
                            </Card>

                            {/* TPU Compute Nodes - Advanced Mode Only */}
                            {advanced && (
                                <Card style={cardStyle}>
                                    <CardHeader
                                        title={
                                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                                <Box display="flex" alignItems="center">
                                                    <Icon className="mr-8">memory</Icon>
                                                    <Typography style={sectionTitleStyle}>TPU Compute Nodes</Typography>
                                                    <HelpDialog 
                                                        title="TPU (Tensor Processing Unit) Compute Nodes"
                                                        content={`TPUs are Google's custom-developed ASICs designed to accelerate machine learning workloads.\n\nKey features:\n• High-bandwidth memory for large models\n• Optimized for deep learning training and inference\n• Connected in groups called slices for distributed training\n\nRequirements:\n• Specific GCP regions/zones (see availability)\n• Requires matching network configuration\n• Uses Google-managed runtime images, not custom stacks`}
                                                    >
                                                        <Icon style={tooltipIconStyle}>help_outline</Icon>
                                                    </HelpDialog>
                                                </Box>
                                            </Box>
                                        }
                                        style={cardHeaderStyle}
                                    />
                                    <CardContent>
                                        <div className="flex flex-row my-16 w-full items-center">
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={form.data?.tpu?.enabled || false}
                                                        onChange={event => {
                                                            const newValue = event.target.checked;
                                                            setForm(prevForm => ({
                                                                ...prevForm,
                                                                data: {
                                                                    ...prevForm.data,
                                                                    tpu: {
                                                                        ...prevForm.data?.tpu,
                                                                        enabled: newValue
                                                                    }
                                                                }
                                                            }));
                                                        }}
                                                        name="tpu_enabled"
                                                        color="primary"
                                                    />
                                                }
                                                label="Enable TPU"
                                                labelPlacement="end"
                                            />
                                        </div>

                                        {form.data?.tpu?.enabled && (
                                            <>
                                                <div className="flex flex-row my-16 w-full">
                                                    <SelectFormsy
                                                        className={clsx(classes.input, 'w-1/3 mr-8')}
                                                        id="gcp_tpu_accelerator_type"
                                                        name="tpu_accelerator_type"
                                                        label="Accelerator Type *"
                                                        type="select"
                                                        variant="outlined"
                                                        value={form.data?.tpu?.accelerator_type || 'v2-8'}
                                                        onChange={e => setForm(prevForm => ({
                                                            ...prevForm,
                                                            data: {
                                                                ...prevForm.data,
                                                                tpu: {
                                                                    ...prevForm.data?.tpu,
                                                                    accelerator_type: e.target.value
                                                                }
                                                            }
                                                        }))}
                                                        required
                                                        MenuProps={{
                                                            PaperProps: {
                                                                style: { maxHeight: 300 }
                                                            }
                                                        }}
                                                    >
                                                        <MenuItem value="v2-8">TPU v2-8</MenuItem>
                                                        <MenuItem value="v2-32">TPU v2-32</MenuItem>
                                                        <MenuItem value="v2-128">TPU v2-128</MenuItem>
                                                        <MenuItem value="v2-256">TPU v2-256</MenuItem>
                                                        <MenuItem value="v2-512">TPU v2-512</MenuItem>
                                                        <MenuItem value="v3-8">TPU v3-8</MenuItem>
                                                        <MenuItem value="v4-8">TPU v4-8</MenuItem>
                                                        <MenuItem value="v5litepod-1">TPU v5e Lite Pod 1</MenuItem>
                                                        <MenuItem value="v5litepod-4">TPU v5e Lite Pod 4</MenuItem>
                                                        <MenuItem value="v5litepod-8">TPU v5e Lite Pod 8</MenuItem>
                                                        <MenuItem value="v5p-8">TPU v5p 8</MenuItem>
                                                        <MenuItem value="v6e-1">TPU v6e 1</MenuItem>
                                                    </SelectFormsy>

                                                    <SelectFormsy
                                                        className={clsx(classes.input, 'w-1/3 mr-8 ml-8')}
                                                        id="gcp_tpu_runtime_version"
                                                        name="tpu_runtime_version"
                                                        label="Runtime Version *"
                                                        type="select"
                                                        variant="outlined"
                                                        value={form.data?.tpu?.runtime_version || 'tpu-vm-tf-2.13.0'}
                                                        onChange={e => setForm(prevForm => ({
                                                            ...prevForm,
                                                            data: {
                                                                ...prevForm.data,
                                                                tpu: {
                                                                    ...prevForm.data?.tpu,
                                                                    runtime_version: e.target.value
                                                                }
                                                            }
                                                        }))}
                                                        required
                                                        MenuProps={{
                                                            PaperProps: {
                                                                style: { maxHeight: 300 }
                                                            }
                                                        }}
                                                    >
                                                        <MenuItem value="tpu-vm-tf-2.13.0">TensorFlow 2.13</MenuItem>
                                                        <MenuItem value="tpu-vm-tf-2.14.0">TensorFlow 2.14</MenuItem>
                                                        <MenuItem value="tpu-vm-pt-2.1">PyTorch 2.1</MenuItem>
                                                        <MenuItem value="tpu-vm-tf-2.12.0">TensorFlow 2.12</MenuItem>
                                                    </SelectFormsy>

                                                    <TextFieldFormsy
                                                        className={clsx(classes.input, 'w-1/3 ml-8')}
                                                        id="gcp_tpu_count"
                                                        name="tpu_count"
                                                        label="Number of TPU Nodes"
                                                        type="number"
                                                        variant="outlined"
                                                        value={form.data?.tpu?.count || 1}
                                                        onChange={e => setForm(prevForm => ({
                                                            ...prevForm,
                                                            data: {
                                                                ...prevForm.data,
                                                                tpu: {
                                                                    ...prevForm.data?.tpu,
                                                                    count: parseInt(e.target.value) || 1
                                                                }
                                                            }
                                                        }))}
                                                        inputProps={{
                                                            min: 1,
                                                            max: 100
                                                        }}
                                                    />
                                                </div>

                                                <div className="flex flex-row my-16 w-full">
                                                    <TextFieldFormsy
                                                        className={clsx(classes.input, 'w-full')}
                                                        id="gcp_tpu_cidr_block"
                                                        name="tpu_cidr_block"
                                                        label="CIDR Block (Optional - Auto-assigned if empty)"
                                                        type="text"
                                                        variant="outlined"
                                                        value={form.data?.tpu?.cidr_block || ''}
                                                        onChange={e => setForm(prevForm => ({
                                                            ...prevForm,
                                                            data: {
                                                                ...prevForm.data,
                                                                tpu: {
                                                                    ...prevForm.data?.tpu,
                                                                    cidr_block: e.target.value || null
                                                                }
                                                            }
                                                        }))}
                                                        placeholder="e.g., 10.128.0.0/29"
                                                        helperText="Leave empty for automatic assignment (10.128+idx.0.0/29)"
                                                    />
                                                </div>

                                                <div className="flex flex-row my-16 w-full items-center">
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={form.data?.tpu?.enable_external_ips || false}
                                                                onChange={event => {
                                                                    const newValue = event.target.checked;
                                                                    setForm(prevForm => ({
                                                                        ...prevForm,
                                                                        data: {
                                                                            ...prevForm.data,
                                                                            tpu: {
                                                                                ...prevForm.data?.tpu,
                                                                                enable_external_ips: newValue
                                                                            }
                                                                        }
                                                                    }));
                                                                }}
                                                                name="tpu_enable_external_ips"
                                                                color="primary"
                                                            />
                                                        }
                                                        label="Enable External IPs"
                                                        labelPlacement="end"
                                                        className="mr-24"
                                                    />

                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={form.data?.tpu?.preemptible || false}
                                                                onChange={event => {
                                                                    const newValue = event.target.checked;
                                                                    setForm(prevForm => ({
                                                                        ...prevForm,
                                                                        data: {
                                                                            ...prevForm.data,
                                                                            tpu: {
                                                                                ...prevForm.data?.tpu,
                                                                                preemptible: newValue
                                                                            }
                                                                        }
                                                                    }));
                                                                }}
                                                                name="tpu_preemptible"
                                                                color="primary"
                                                            />
                                                        }
                                                        label="Preemptible"
                                                        labelPlacement="end"
                                                        className="mr-24"
                                                    />

                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={form.data?.tpu?.spot || false}
                                                                onChange={event => {
                                                                    const newValue = event.target.checked;
                                                                    setForm(prevForm => ({
                                                                        ...prevForm,
                                                                        data: {
                                                                            ...prevForm.data,
                                                                            tpu: {
                                                                                ...prevForm.data?.tpu,
                                                                                spot: newValue
                                                                            }
                                                                        }
                                                                    }));
                                                                }}
                                                                name="tpu_spot"
                                                                color="primary"
                                                            />
                                                        }
                                                        label="Spot"
                                                        labelPlacement="end"
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </CardContent>
                                </Card>
                            )}

                            {/* Description */}
                            <Card style={cardStyle}>
                                <CardHeader
                                    title={
                                        <Box display="flex" alignItems="center">
                                            <Icon className="mr-8">description</Icon>
                                            <Typography style={sectionTitleStyle}>Description</Typography>
                                        </Box>
                                    }
                                    style={cardHeaderStyle}
                                />
                                <CardContent>
                                    <TextField
                                        className={clsx(classes.input, 'my-16 w-full')}
                                        id="gcp_description"
                                        name="description"
                                        label="Description"
                                        type="textarea"
                                        variant="outlined"
                                        value={form.description}
                                        onChange={handleChange}
                                        fullWidth
                                        multiline
                                        minRows={3}
                                    />
                                </CardContent>
                            </Card>
                        </Formsy>
                    </DisabledFormWrapper>
                </DialogContent>
                <ScriptDialog
                    open={isScriptOpen}
                    onClose={() => setScriptOpen(false)}
                    scriptContent={form?.data?.provision?.script || ''}
                />
            </React.Fragment>
        )}
    </React.Fragment>
    );
};

export default GcpCluster;
